/**
 * Atelier Guillot — personnalisation avec aperçu en direct sur la photo du produit.
 * Base : atelier-mino/theme/assets/mino-apercu.js (calque SVG en « slice », fondu, préchargement, délais en setTimeout).
 *
 * Pièce = un support (porte-carte grise « pcg », porte-documents « pd », porte-clés « pc ») et son état : coloris, marque,
 * modèle, texte en bas, verso, porte-clés assorti. La classe Piece ne connaît ni le panier ni la page : elle dessine et rend
 * ses propriétés au format du contrat (data/contrat-proprietes.json). Le futur configurateur de coffret empilera des pièces.
 *
 * Rendu : image vierge du coloris + calque SVG (viewBox 0–100, comme une image carrée en object-fit: cover) :
 *   gravure (logo, logo de modèle, texte) dans la teinte mesurée du coloris (multiply si la gravure fonce, normal si elle
 *   éclaircit) ; décor (titre de la maquette, cadre « préparé par l'atelier ») en mode normal.
 * Tant que l'état est celui de la photo de la fiche (fiche de marque, coloris de la photo, rien ajouté), la photo d'origine
 * reste affichée dans la galerie.
 */
import { CartUpdatedEvent } from '@theme/events';

const SVG = 'http://www.w3.org/2000/svg';
const CAPITALE = 0.7;                       // hauteur des capitales de Barlow (700/1000 em)
const INTERLETTRAGE = -0.025;               // em : Barlow SemiBold est 4,7 % plus large que DIN Alternate Bold
const POLICE = "600 100px GuillotGravure";
// Polices du verso du porte-clés (validées par Thomas le 22/10/2026, disponibles dans Canva) : famille web, graisse, hauteur
// des capitales (em), interlettrage, et nom envoyé dans la commande pour que l'atelier prenne la même dans Canva
const POLICES = {
  classique: { f: 'GuillotGravure', w: 600, cap: 0.7, il: -0.025, nom: 'Classique (Barlow)' },
  manuscrite: { f: 'GuillotManuscrite', w: 700, cap: 0.58, il: 0, nom: 'Manuscrite (Dancing Script)' },
  elegante: { f: 'GuillotElegante', w: 700, cap: 0.708, il: 0, nom: 'Élégante (Playfair Display)' },
};
const mesure = document.createElement('canvas').getContext('2d');
const reduit = matchMedia('(prefers-reduced-motion: reduce)');
// Caractères gravables : lettres latines (accents compris), chiffres, ponctuation courante, cœurs (vus en commande, H9)
const GRAVABLE = /[\p{Script=Latin}\p{Nd} .,;:'’"«»&/\\\-_#+()!?@°*=%€$♥♡❤️]/u;

const cacheLogos = new Map();
const cacheJson = new Map();

function charger(url) {
  if (!cacheJson.has(url)) {
    // échec (réseau, fichier pas encore servi par le CDN) : oublié, pour réessayer au prochain appel
    cacheJson.set(url, fetch(url).then((r) => (r.ok ? r.json() : Promise.reject(r.status))).catch((e) => { cacheJson.delete(url); throw e; }));
  }
  return cacheJson.get(url);
}

/** Logo SVG de l'atelier : { d, tr, encre:[x0,y0,x1,y1] } dans un cadre 1080 × 1080. */
function logo(url) {
  if (!url) return Promise.resolve(null);
  if (!cacheLogos.has(url)) {
    cacheLogos.set(url, fetch(url).then((r) => r.text()).then((txt) => {
      const doc = new DOMParser().parseFromString(txt, 'image/svg+xml');
      const s = doc.documentElement, p = s.querySelector('path');
      const rel = s.hasAttribute('data-largeur-marque') ? { l: +s.getAttribute('data-largeur-marque'), e: +s.getAttribute('data-ecart-marque'), c: s.getAttribute('data-cale-marque') } : null;
      const axe = s.hasAttribute('data-axe') ? +s.getAttribute('data-axe') : null;   // axe optique (centre de masse), scripts/axes_logos.py
      return { d: p.getAttribute('d'), tr: p.getAttribute('transform') || '', encre: (s.getAttribute('data-encre') || '0 0 1080 1080').split(' ').map(Number), rel, axe };
    }).catch(() => null));
  }
  return cacheLogos.get(url);
}

export function normaliser(s) {
  return (s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/\s+/g, ' ').trim();
}

/** Texte saisi : espaces réduits ; renvoie aussi les caractères non gravables. */
export function verifierTexte(brut, max) {
  const texte = (brut || '').replace(/\s+/g, ' ').trim();
  const refus = [...new Set([...texte].filter((c) => !GRAVABLE.test(c)))];
  const n = [...texte.replace(/️/g, '')].length;
  return { texte, refus, n, trop: n > max };
}

/** Texte saisi nettoyé : espaces réduits ; retours à la ligne gardés si `lignes` > 1 (verso du porte-clés), au plus `lignes`. */
export function nettoyer(brut, lignes = 1) {
  const l = (brut || '').split(/\r?\n/).map((x) => x.replace(/\s+/g, ' ').trim()).filter(Boolean);
  return lignes > 1 ? [...l.slice(0, lignes - 1), l.slice(lignes - 1).join(' ')].filter(Boolean).join('\n') : l.join(' ');
}

/** Ajout au panier : en multipart quand des fichiers sont joints à la ligne principale (Shopify les stocke sur son CDN et
 *  met leur adresse dans la propriété), sinon en JSON comme avant. */
async function envoyerPanier(url, items, sections, fichiers = []) {
  let corps, entetes = { Accept: 'application/json' };
  if (fichiers.length) {
    corps = new FormData();
    items.forEach((it, i) => {
      corps.append(`items[${i}][id]`, it.id);
      corps.append(`items[${i}][quantity]`, it.quantity);
      Object.entries(it.properties || {}).forEach(([k, v]) => corps.append(`items[${i}][properties][${k}]`, v));
    });
    fichiers.forEach((x) => corps.append(`items[0][properties][${x.cle}]`, x.blob, x.nom));
    corps.append('sections', sections);
  } else {
    corps = JSON.stringify({ items, sections });
    entetes['Content-Type'] = 'application/json';
  }
  return fetch(url, { method: 'POST', headers: entetes, body: corps });
}

/** Fichiers de gravure d'une pièce, nommés pour la commande ; jamais bloquant (erreur → pas de fichier). */
async function fichiersPiece(piece, cle, nomBase) {
  try {
    return (await piece.fichiers()).map((x) => ({ cle: x.nom === 'verso' ? `${cle} verso` : cle, blob: x.blob, nom: `${nomBase}-${x.nom}.jpg` }));
  } catch (err) { console.warn('guillot-perso : fichier de gravure non généré', err); return []; }
}

/** Écrit la densité (dpi) dans l'en-tête JFIF d'un JPG produit par un canevas (sinon 72 dpi supposés). */
async function jpegDpi(blob, dpi) {
  const b = new Uint8Array(await blob.arrayBuffer());
  if (b[2] === 0xff && b[3] === 0xe0 && b[6] === 0x4a && b[7] === 0x46) {   // APP0 « JFIF »
    b[13] = 1; b[14] = dpi >> 8; b[15] = dpi & 255; b[16] = dpi >> 8; b[17] = dpi & 255;
  }
  return new Blob([b], { type: 'image/jpeg' });
}

function nomAffiche(marque) {
  return (marque || '').replace(/\s*\(.*?\)\s*/g, ' ').trim();
}

/* ------------------------------------------------------------------ Pièce : état, rendu, propriétés */
export class Piece {
  constructor(donnees) {
    this.d = donnees;
    this.s = donnees.supports[donnees.fiche.support];
    const f = donnees.fiche;
    this.etat = {
      coloris: f.coloris_defaut || this.s.defaut, marque: f.marque || '', logo: f.logo || null, logoUrl: donnees.logo_fiche,
      modele: '', modeleOn: false, logoModele: null, modeleLibre: false, texte: '', texteOn: false, verso: '', versoOn: false,
      porteCles: '', face: 'recto', mm: f.taille_mm || null, recto: '', rectoOn: false, variante: false, varianteNom: '', police: 'classique', haut: '', bas: '',
    };
  }

  /** Variante réellement gravée : Audi « anneaux et nom » devient « anneaux seuls » quand un logo de modèle est dessous
   *  (même règle que le dessin, retour de Victor du 20/10/2026). */
  varianteGravee() {
    const e = this.etat, f = this.d.fiche;
    const avecModele = (e.modeleOn && e.modele && e.logoModele) || (f.modele && e.marque === (f.marque || ''));
    return avecModele && e.logo === 'audi' ? 'Anneaux seuls' : e.varianteNom;
  }

  /** Porte-clés d'un coffret : texte gravé à la place du logo (« Je veux autre chose qu'un logo » d'Aris). */
  rectoTexte() { return this.etat.rectoOn ? (this.etat.recto || '').trim() : ''; }

  coloris() { return this.s.coloris.find((c) => c.v === this.etat.coloris) || this.s.coloris[0]; }

  /** Vrai tant que l'aperçu serait identique à la photo de la fiche (rien à superposer). */
  commeLaPhoto() {
    const f = this.d.fiche, e = this.etat;
    const perso = f.config.includes('perso');
    return (!perso || !e.marque) && e.coloris === (f.coloris_defaut || this.s.defaut) && e.logo === (f.logo || null) && e.marque === (f.marque || '')
      && !(e.modeleOn && e.modele) && !(e.texteOn && e.texte) && !(e.versoOn && e.verso && e.face === 'verso') && !e.porteCles
      && !this.rectoTexte() && !(e.haut || '').trim() && !(e.bas || '').trim();
  }

  /** Propriétés de la ligne principale, dans l'ordre du contrat (sans _apo_order, ajouté par le panier). */
  proprietes() {
    const f = this.d.fiche, e = this.etat, cfg = f.config, o = {};
    const texte = e.texteOn ? e.texte : '', modele = e.modeleOn ? e.modele : '', verso = e.versoOn ? e.verso : '';
    const marqueChangee = e.marque !== (f.marque || '');
    if (cfg === 'pcg-marque' || cfg === 'pd-marque') {
      o[cfg === 'pcg-marque' ? 'Couleur des surpiqures' : 'Couleur'] = e.coloris;   // en 1er : outil de tri (H5)
      if (cfg === 'pcg-marque') o['Porte clefs'] = e.porteCles || '';
      if (marqueChangee) o['La marque de votre vehicule'] = e.marque;
      if (texte) o["Ajout d'un texte en bas"] = texte;
      if (modele) o['Personnalisation logo supplémentaire'] = modele;
    } else if (cfg === 'pcg-perso' || cfg === 'pd-perso') {
      o['La marque de votre vehicule'] = e.marque;
      if (texte) o["Ajout d'un texte en bas"] = texte;
      if (modele) o['Personnalisation logo supplémentaire'] = modele;
      o[cfg === 'pcg-perso' ? 'Couleur des surpiqures' : 'Couleur'] = e.coloris;
      if (cfg === 'pcg-perso' && e.porteCles) o['Porte clefs'] = e.porteCles;
    } else if (cfg === 'pc-marque') {
      o['Choisissez votre couleur'] = e.coloris;
      if (marqueChangee) o['La marque de votre vehicule'] = e.marque;      // nouveau (H5) : couleur toujours en 1er
      o['Personnalisation du Verso'] = verso ? 'Ajouter' : '';
      if (verso) { o['Personnalisation du verso 2€'] = verso; o['Police du verso'] = (POLICES[e.police] || POLICES.classique).nom; }
    } else if (cfg === 'da-perso') {
      if ((e.haut || '').trim()) o['Texte en haut'] = e.haut.trim();
      if ((e.bas || '').trim()) o['Texte en bas'] = e.bas.trim();
    } else if (cfg === 'pcb-marque') {
      o.Couleur = e.coloris;
      o['Gravure au verso'] = verso;
    } else if (cfg === 'pc-perso') {
      o.Couleur = e.coloris;
      o['La marque de votre vehicule'] = e.marque;
      o['Personnalisation du Verso'] = verso ? 'Ajouter' : '';
      if (verso) { o['Personnalisation du verso 2€'] = verso; o['Police du verso'] = (POLICES[e.police] || POLICES.classique).nom; }
    }
    // variante du logo choisie parmi celles de la marque (nouvelle clé, en dernier : l'outil de tri lit la première)
    if (e.varianteNom && !this.rectoTexte()) o['Variante du logo'] = this.varianteGravee();
    return o;
  }

  /** Pièce d'un coffret : clés claires préfixées par la pièce (« Porte-carte grise – Marque »), valeurs en français. */
  proprietesCoffret(L) {
    const e = this.etat, s = this.d.fiche.support, o = {}, recto = this.rectoTexte();
    o[`${L} – ${s === 'pcg' ? 'Surpiqûres' : 'Couleur'}`] = e.coloris;
    if (recto) o[`${L} – Texte à la place du logo`] = recto;
    else { o[`${L} – Marque`] = e.marque; if (e.varianteNom) o[`${L} – Logo`] = this.varianteGravee(); }
    if (e.modeleOn && e.modele) o[`${L} – Logo du modèle`] = e.modele;
    if (e.texteOn && e.texte) o[`${L} – Texte en bas`] = e.texte;
    if (e.versoOn && e.verso) { o[`${L} – Verso`] = e.verso; if (s === 'pc') o[`${L} – Police du verso`] = (POLICES[e.police] || POLICES.classique).nom; }
    return o;
  }

  /** Produit caché du texte en bas : celui de l'ensemble Aris de la fiche (Ford Alps Runners : texte_bas_2), sinon le commun. */
  optionTexte() { return this.d.options[this.d.fiche.option_texte] || this.d.options.texte_bas; }

  /** Produit caché du verso : porte-carte bancaire (gravure 3 €), porte-clé personnalisé, porte-clé de marque. */
  optionVerso() {
    const op = this.d.options, cfg = this.d.fiche.config;
    return cfg.startsWith('pcb') ? op.gravure_verso : cfg === 'pc-perso' ? op.verso_perso : op.verso_marque;
  }

  /** Options payantes (produits cachés d'Aris) : [{ id, prix, apo }]. */
  options() {
    const op = this.d.options, e = this.etat, cfg = this.d.fiche.config, out = [];
    if (e.porteCles && cfg.startsWith('pcg')) {
      const v = op.porte_clefs.variantes[e.porteCles];
      if (v) out.push({ id: v.id, prix: v.prix, apo: `Porte clefs: ${e.porteCles}` });
    }
    if (e.marque === op.logo_par_mail.valeur) out.push({ id: op.logo_par_mail.id, prix: op.logo_par_mail.prix, apo: `${op.logo_par_mail.libelle}: ${op.logo_par_mail.valeur}` });
    if (e.texteOn && e.texte) { const t = this.optionTexte(); out.push({ id: t.id, prix: t.prix, apo: `${t.libelle}: ` }); }
    // pièce d'un coffret en « même gravure » : logo du modèle inclus (décision de Thomas du 22/10/2026)
    if (e.modeleOn && e.modele && !this.modeleInclus) out.push({ id: op.logo_supp.id, prix: op.logo_supp.prix, apo: `${op.logo_supp.libelle}: ` });
    if (e.versoOn && e.verso) {
      const v = this.optionVerso();
      out.push({ id: v.id, prix: v.prix, apo: `${v.libelle}: ${v.valeur}` });
    }
    return out;
  }

  /** Zones du logo et du modèle à la taille du logo (H28) : de 50 à 65 mm selon sa lisibilité sur le porte-carte et le
   *  porte-documents ; le carré grandit autour de son centre et la zone du modèle descend d'autant. Porte-clés : taille fixe. */
  zonesLogo() {
    const z = this.s.zones, mm = this.etat.mm, sup = this.d.fiche.support;
    // porte-documents : logos de marque réduits de 15 % (cadre ≈ 88 mm au lieu de 104 ; BMW, Tesla, McLaren trop gros,
    // retour de Victor du 20/10/2026) ; porte-carte grise : 50 à 65 mm selon la lisibilité du logo (H28)
    const k = sup === 'pd' ? 0.85 : sup === 'pcg' && mm && mm !== 50 ? mm / 50 : 1;
    if (k === 1 || !z.logo) return { logo: z.logo, modele: z.modele };
    const cx = (z.logo.x0 + z.logo.x1) / 2, cy = (z.logo.y0 + z.logo.y1) / 2;
    const w = (z.logo.x1 - z.logo.x0) * k, h = (z.logo.y1 - z.logo.y0) * k;
    const logo = { x0: cx - w / 2, x1: cx + w / 2, y0: cy - h / 2, y1: cy + h / 2 };
    const d = logo.y1 - z.logo.y1;
    const modele = z.modele && { ...z.modele, y0: z.modele.y0 + d, y1: z.modele.y1 + d };
    return { logo, modele };
  }

  /** Dessine la pièce dans un hôte (cadre carré ou rectangulaire) : image vierge + calques. */
  async dessiner(hote, { taille = 'img', production = false, face = null } = {}) {
    const e = this.etat, c = this.coloris(), zl = this.zonesLogo(), z = { ...this.s.zones, logo: zl.logo, modele: zl.modele };
    let img = hote.querySelector(':scope > img.gp-vierge');
    if (!img) {
      img = document.createElement('img');
      img.className = 'gp-vierge';
      img.alt = '';
      img.decoding = 'async';
      hote.appendChild(img);
      ['gp-gravure', 'gp-deco'].forEach((cl) => {
        const s = document.createElementNS(SVG, 'svg');
        s.setAttribute('class', cl);
        s.setAttribute('viewBox', '0 0 100 100');
        s.setAttribute('preserveAspectRatio', 'xMidYMid slice');
        s.setAttribute('aria-hidden', 'true');
        hote.appendChild(s);
      });
    }
    const src = taille === 'mini' ? c.mini : c.img;
    if (img.getAttribute('src') !== src) {
      hote.classList.add('gp-attente');
      img.setAttribute('src', src);
      const fin = () => hote.classList.remove('gp-attente');
      (img.decode ? img.decode() : Promise.resolve()).then(fin, fin);
      setTimeout(fin, 1500);   // jamais d'image masquée durablement (onglet en arrière-plan)
    }
    const verso = (face || e.face) === 'verso' && this.d.fiche.support === 'pc';
    hote.classList.toggle('gp-verso', verso);
    const grav = hote.querySelector(':scope > .gp-gravure'), deco = hote.querySelector(':scope > .gp-deco');
    grav.style.mixBlendMode = c.t.m;
    grav.style.opacity = c.t.o;
    if (this.d.fiche.support === 'da') return this.dessinerDisque(hote, grav, deco, c);
    const recto = verso ? '' : this.rectoTexte();
    // marque dont le logo complet devient trop chargé avec un modèle dessous : variante plus sobre (Audi : les 4 anneaux seuls,
    // retour de Victor du 20/10/2026)
    const avecModele = (e.modeleOn && e.modele && e.logoModele) || (this.d.fiche.modele && e.marque === (this.d.fiche.marque || ''));
    const urlMarque = avecModele && e.logoUrl ? e.logoUrl.replace(/gl-audi\.svg/, 'gl-audi-anneaux.svg') : e.logoUrl;
    const [lg, lm] = await Promise.all([verso || recto ? null : logo(urlMarque), verso ? null : logo(e.logoModele)]);
    const g = document.createDocumentFragment(), dd = document.createDocumentFragment();
    // logo_cadre (métachamp de la fiche) : boîte d'encre du logo mesurée sur la photo de la fiche, en % de l'image ; le logo
    // y est ajusté tel quel (Pack M : le M fait 60 % de la largeur du cuir, retour de Thomas du 18/10/2026)
    const cadreFiche = !verso && this.d.fiche.logo_cadre && e.marque === (this.d.fiche.marque || '') && e.logo === this.d.fiche.logo;
    if (lg && cadreFiche) { const [x0, y0, x1, y1] = this.d.fiche.logo_cadre; g.appendChild(this.nLogo(lg, { x0, y0, x1, y1 }, c.t.c, true)); }
    // porte-clés d'un coffret avec logo du modèle : marque + modèle réunis dans le carré (retour de Thomas du 22/10/2026)
    else if (lg && lm && z.logo && !z.modele && this.d.fiche.support === 'pc' && e.modeleOn && e.modele) g.appendChild(this.nMarqueModele(lg, lm, z.logo, c.t.c));
    else if (lg && z.logo) g.appendChild(this.nLogo(lg, z.logo, c.t.c, false));
    if (lg && lg.encre && z.logo && z.modele) {
      // logo du modèle collé sous l'encre du logo de la marque (≈ 4 mm), et non sous le carré entier
      const hl = z.logo.y1 - z.logo.y0, cote = Math.min(z.logo.x1 - z.logo.x0, hl);
      const bas = z.logo.y0 + (hl - cote) / 2 + (lg.encre[3] / 1080) * cote, ecart = cote * 4 / 50;
      if (bas + ecart < z.modele.y0) z.modele = { ...z.modele, y0: bas + ecart, y1: bas + ecart + (z.modele.y1 - z.modele.y0) };
    }
    const f = this.d.fiche, ficheIntacte = e.marque === (f.marque || '');
    let modele = null;   // { l, zone } : logo de modèle gravé, repris sur le porte-clés assorti
    if (!verso && z.modele && e.modeleOn && e.modele) {
      if (lm) g.appendChild(this.nLogo(lm, (modele = { l: lm, zone: this.zoneRelative(lm, lg, z) || this.zoneModele(lm, lg, z) }).zone, c.t.c, true));
      else dd.appendChild(this.nAtelier(z.modele, e.modele));
    } else if (!verso && z.modele && ficheIntacte && f.modele) {
      // fiche modèle : logo du modèle de l'atelier s'il existe, sinon le modèle en texte, comme sur la photo de la fiche
      const lf = await logo(this.d.logo_modele_fiche);
      if (lf) g.appendChild(this.nLogo(lf, (modele = { l: lf, zone: this.zoneRelative(lf, lg, z) || this.zoneModele(lf, lg, z) }).zone, c.t.c, true));
      else if (f.modele_texte !== false) g.appendChild(this.nTexteZone(z.modele, f.modele.toLocaleUpperCase('fr'), c.t.c, z.texte?.capitale));
    }
    if (recto && z.logo) {
      // texte gravé dans le carré du logo, à la taille du texte du verso
      const gr = [...recto].filter((ch) => GRAVABLE.test(ch)).join('').replace(/\s+/g, ' ').trim(), cap = z.verso?.capitale || 4;
      if (gr) g.appendChild(this.nTexte({ cx: (z.logo.x0 + z.logo.x1) / 2, ligne: (z.logo.y0 + z.logo.y1) / 2 + cap / 2, capitale: cap, x0: z.logo.x0, x1: z.logo.x1 }, gr, c.t.c));
    } else if (!verso && !lg && e.marque && z.logo) dd.appendChild(this.nAtelier(z.logo, nomAffiche(e.marque)));
    // seuls les caractères gravables sont dessinés (l'emoji refusé n'apparaît pas sur l'aperçu)
    const brut = verso ? (e.versoOn ? e.verso : '') : (e.texteOn ? e.texte : '');
    const texte = nettoyer([...brut].filter((ch) => ch === '\n' || GRAVABLE.test(ch)).join(''), verso ? 3 : 1);
    // verso : la photo est retournée en miroir (H26 de Mino) ; la zone l'est aussi
    const zt = verso && z.verso ? { ...z.verso, cx: 100 - z.verso.cx, x0: 100 - z.verso.x1, x1: 100 - z.verso.x0 } : z.texte;
    if (texte && zt) {
      // Porte-carte grise : la gravure est ton sur ton (noir sur noir) et le texte de 18 pt serait illisible sur l'aperçu. Il est
      // donc MIS EN VALEUR (clair, agrandi, cadre pointillé, comme le modèle), avec une mention sous le champ (Thomas, 17/10/2026).
      if (!verso && this.s.texte_valeur && !production) dd.appendChild(this.nTexteValeur(zt, texte));
      else {
        const pol = verso && e.police !== 'classique' ? POLICES[e.police] : null;
        if (texte.includes('\n')) g.appendChild(this.nTexteLignes(zt, texte.split('\n'), c.t.c, pol));
        else g.appendChild(this.nTexte(zt, texte, c.t.c, pol));
      }
    }
    if (z.titre && !verso) dd.appendChild(this.nTitre(z.titre));
    if (!verso && e.porteCles && this.s.porte_cles_visuel) {
      const n = this.nPorteCles(lg, modele, z.logo);
      if (n) dd.appendChild(n);
    }
    grav.replaceChildren(g);
    deco.replaceChildren(dd);
    hote.dataset.gpCle = this.cle();
    return { grav, deco };
  }

  cle() { return JSON.stringify(this.etat); }

  /* ---------- Disque A personnalisé (22/10/2026) : « Texte en haut » et « Texte en bas » en capitales, en arc le long du disque
   * (effet « courbe » de Canva), police Luckiest Guy (la plus proche de Funtastic, la police de l'atelier dans Canva). */
  dessinerDisque(hote, grav, deco, c) {
    const D = this.s.zones.disque, e = this.etat, g = document.createDocumentFragment();
    const cap = D.r * 0.14;   // hauteur des capitales
    const arc = (texte, enHaut) => {
      const t = nettoyer(texte).toLocaleUpperCase('fr');
      if (!t) return;
      const pol = { f: 'GuillotDisque', w: 400, cap: 0.75 };
      let fs = cap / pol.cap;
      mesure.font = `${pol.w} 100px ${pol.f}`;
      const R = enHaut ? D.r * 0.8 : D.r * 0.93;           // ligne de base : haut = arc intérieur, bas = arc extérieur (au-dessus et au-dessous du A)
      const larg = (ch) => (mesure.measureText(ch).width * fs) / 100;
      let total = [...t].reduce((a, ch) => a + larg(ch), 0);
      const max = (enHaut ? 1.95 : 1.75) * R;             // ≈ 110° et 100° d'ouverture au plus
      if (total > max) { fs *= max / total; total = max; }
      let pos = -total / 2;
      for (const ch of t) {
        const w = larg(ch), a = (pos + w / 2) / R;          // angle depuis le haut (ou le bas) du disque
        pos += w;
        const th = enHaut ? -Math.PI / 2 + a : Math.PI / 2 - a;
        const x = D.cx + R * Math.cos(th), y = D.cy + R * Math.sin(th);
        const rot = (enHaut ? a : -a) * 180 / Math.PI;
        const n = document.createElementNS(SVG, 'text');
        n.setAttribute('x', x.toFixed(3)); n.setAttribute('y', y.toFixed(3));
        n.setAttribute('text-anchor', 'middle');
        n.setAttribute('font-size', fs.toFixed(3));
        n.setAttribute('fill', c.t.c);
        n.setAttribute('transform', `rotate(${rot.toFixed(2)} ${x.toFixed(3)} ${y.toFixed(3)})`);
        n.setAttribute('class', 'gp-texte');
        n.style.fontFamily = `'${pol.f}', sans-serif`; n.style.fontWeight = pol.w; n.style.letterSpacing = '0em';
        n.textContent = ch;
        g.appendChild(n);
      }
    };
    arc(e.haut, true);
    arc(e.bas, false);
    grav.replaceChildren(g);
    deco.replaceChildren();
    hote.dataset.gpCle = this.cle();
    return { grav, deco };
  }

  /* ---------- fichier de gravure joint à la commande (demande de Thomas du 22/10/2026) : JPG noir sur blanc à 600 dpi, au
   * format exact du support (porte-carte grise 103,4 × 144,39 mm, porte-documents 160 × 230 mm, porte-clés 30 × 30 mm recto
   * et verso). Même dessin que l'aperçu (logo, variante, modèle, textes, polices), sans le décor. */
  async fichiers({ dpi = 600 } = {}) {
    const f = this.d.fiche, mm = this.s?.mm;
    if (!mm || f.apercu === false) return [];   // fiches club, porte-carte bancaire : pas de géométrie
    const out = [], sup = f.support, e = this.etat;
    const pct = (xmm, ymm) => [((mm.o[0] + xmm * mm.k[0]) / mm.t[0]) * 100, ((mm.o[1] + ymm * mm.k[1]) / mm.t[1]) * 100];
    const versMm = (xp, yp) => [((xp / 100) * mm.t[0] - mm.o[0]) / mm.k[0], ((yp / 100) * mm.t[1] - mm.o[1]) / mm.k[1]];
    const carre = (cxp, cyp, cote = 30) => { const [x, y] = versMm(cxp, cyp); return [x - cote / 2, y - cote / 2, cote, cote]; };
    let rect;
    if (sup === 'pc') { const L = this.s.zones.logo; rect = carre((L.x0 + L.x1) / 2, (L.y0 + L.y1) / 2); }
    else rect = [0, 0, (mm.o[2] - mm.o[0]) / mm.k[0], (mm.o[3] - mm.o[1]) / mm.k[1]];
    out.push({ nom: 'recto', blob: await this.rendreFichier(rect, pct, dpi, 'recto') });
    if (sup === 'pc' && e.versoOn && (e.verso || '').trim()) {
      const V = this.s.zones.verso;
      out.push({ nom: 'verso', blob: await this.rendreFichier(carre(100 - V.cx, V.ligne - V.capitale / 2), pct, dpi, 'verso') });
    }
    return out.filter((x) => x.blob);
  }

  async rendreFichier([x, y, w, h], pct, dpi, face) {
    const hote = document.createElement('div');
    const r = await this.dessiner(hote, { production: true, face });
    const [px0, py0] = pct(x, y), [px1, py1] = pct(x + w, y + h);
    const W = Math.round((w / 25.4) * dpi), H = Math.round((h / 25.4) * dpi);
    // logos : le calque SVG est recadré sur le support (viewBox en % de la photo), encre en noir
    const svg = r.grav.cloneNode(true);
    const textes = [...svg.querySelectorAll('text')].map((t) => {
      const st = r.grav.ownerDocument ? t.style : {};
      const rot = /rotate\(([-\d.]+)/.exec(t.getAttribute('transform') || '');
      const rec = { x: +t.getAttribute('x'), y: +t.getAttribute('y'), fs: +t.getAttribute('font-size'), txt: t.textContent, rot: rot ? +rot[1] : 0,
        fam: st.fontFamily || "'GuillotGravure'", poids: st.fontWeight || 600, il: parseFloat(st.letterSpacing) || 0 };
      t.remove();
      return rec;
    });
    svg.querySelectorAll('[fill]').forEach((n) => n.setAttribute('fill', '#000'));
    svg.setAttribute('xmlns', SVG);
    svg.setAttribute('viewBox', `${px0} ${py0} ${px1 - px0} ${py1 - py0}`);
    svg.setAttribute('preserveAspectRatio', 'none');
    svg.setAttribute('width', W);
    svg.setAttribute('height', H);
    svg.removeAttribute('style');
    const cv = document.createElement('canvas');
    cv.width = W; cv.height = H;
    const ctx = cv.getContext('2d');
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, W, H);
    if (this.s.fond_fichier) {   // impression (Disque A) : le fond de la photo vierge fait partie du fichier
      const fond = new Image();
      fond.crossOrigin = 'anonymous';
      fond.src = this.coloris().img;
      await fond.decode().catch(() => {});
      if (fond.naturalWidth) {
        const k = fond.naturalWidth / 100;
        ctx.drawImage(fond, px0 * k, py0 * k, (px1 - px0) * k, (py1 - py0) * k, 0, 0, W, H);
      }
    }
    const url = URL.createObjectURL(new Blob([new XMLSerializer().serializeToString(svg)], { type: 'image/svg+xml' }));
    try {
      const im = new Image();
      im.src = url;
      await im.decode();
      ctx.drawImage(im, 0, 0, W, H);
    } finally { URL.revokeObjectURL(url); }
    // textes : dessinés sur le canevas avec les polices chargées par la page (une image SVG n'y a pas accès)
    const sx = W / (px1 - px0), sy = H / (py1 - py0);
    ctx.fillStyle = '#000';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'alphabetic';
    for (const t of textes) {
      const taille = t.fs * sy, fam = t.fam.replace(/"/g, "'");
      await document.fonts?.load(`${t.poids} ${Math.round(taille)}px ${fam}`).catch(() => {});
      ctx.font = `${t.poids} ${taille}px ${fam}, sans-serif`;
      if ('letterSpacing' in ctx) ctx.letterSpacing = `${t.il * taille}px`;
      ctx.save();
      ctx.translate((t.x - px0) * sx, (t.y - py0) * sy);
      if (t.rot) ctx.rotate((t.rot * Math.PI) / 180);
      ctx.fillText(t.txt, 0, 0);
      ctx.restore();
    }
    const blob = await new Promise((ok) => cv.toBlob(ok, 'image/jpeg', 0.92));
    return blob ? jpegDpi(blob, dpi) : null;
  }

  /** Logo de modèle (bibliothèque) : taille fixée par sa surface et proportionnelle à la largeur du logo de la marque, au lieu de
   *  remplir toute la largeur de la zone (X5 énorme, M340i minuscule : retour de Victor du 20/10/2026). Moyenne géométrique
   *  largeur × hauteur = 44 % de la largeur d'encre de la marque, bornée par la zone ; centré sur l'axe optique de la marque. */
  zoneModele(lm, lg, z) {
    const zm = z.modele, W = zm.x1 - zm.x0, H = zm.y1 - zm.y0;
    const [ex0, ey0, ex1, ey1] = lm.encre, a = (ex1 - ex0) / Math.max(1, ey1 - ey0);
    let cx = (zm.x0 + zm.x1) / 2, largeurMarque = W * 0.9;
    if (lg?.encre && z.logo) {
      const w = z.logo.x1 - z.logo.x0, h = z.logo.y1 - z.logo.y0, cote = Math.min(w, h), ox = z.logo.x0 + (w - cote) / 2;
      largeurMarque = ((lg.encre[2] - lg.encre[0]) / 1080) * cote;
      cx = ox + ((lg.axe ?? (lg.encre[0] + lg.encre[2]) / 2) / 1080) * cote;
    }
    const plafond = this.d.fiche.support === 'pd' ? 0.48 : 0.40;
    const G = Math.min(Math.max(0.44 * largeurMarque, 0.25 * W), plafond * W);
    let lw = G * Math.sqrt(a), lh = G / Math.sqrt(a);
    const r = Math.min(1, W / lw, H / lh);
    lw *= r; lh *= r;
    return { x0: cx - lw / 2, x1: cx + lw / 2, y0: zm.y0, y1: zm.y0 + lh };
  }

  /** Marque et logo du modèle composés comme sur le porte-carte grise (modèle 4 mm sous l'encre de la marque, taille par
   *  zoneModele), puis la composition entière ajustée dans le carré du porte-clés (porte-clés sans zone de modèle). */
  nMarqueModele(lg, lm, zone, couleur) {
    const zl = { x0: 0, y0: 0, x1: 100, y1: 100 }, bas = (lg.encre[3] / 1080) * 100 + 8;
    const zv = { logo: zl, modele: { x0: 0, x1: 100, y0: bas, y1: bas + 44 } };
    const zm = this.zoneRelative(lm, lg, zv) || this.zoneModele(lm, lg, zv);
    const [a, b, c, d] = lg.encre.map((v) => v / 10.8), m = 4;
    const x0 = Math.min(a, zm.x0) - m, x1 = Math.max(c, zm.x1) + m, y0 = b - m, y1 = Math.max(d, zm.y1) + m;
    const s = document.createElementNS(SVG, 'svg');
    s.setAttribute('viewBox', [x0, y0, x1 - x0, y1 - y0].map((v) => v.toFixed(3)).join(' '));
    s.setAttribute('preserveAspectRatio', 'xMidYMid meet');
    [['x', zone.x0], ['y', zone.y0], ['width', zone.x1 - zone.x0], ['height', zone.y1 - zone.y0]].forEach(([k, v]) => s.setAttribute(k, v));
    s.setAttribute('overflow', 'visible');
    s.append(this.nLogo(lg, zl, couleur, false), this.nLogo(lm, zm, couleur, true));
    return s;
  }

  /** Logo dans sa zone. Logo de marque : cadre 1080 entier posé sur le carré de gravure (comme l'atelier pose le PNG). Logo de
   *  modèle : encre ajustée (contain) à la zone sous la marque. */
  /** Logo de modèle redessiné d'après la maquette (data-largeur-marque / data-ecart-marque) : même taille et même écart
   *  sous l'encre du logo de la marque que sur la photo de la fiche (scripts/logos_redessines.py). */
  zoneRelative(lm, lg, z) {
    if (!lm?.rel || !lg?.encre || !z.logo) return null;
    const w = z.logo.x1 - z.logo.x0, h = z.logo.y1 - z.logo.y0, cote = Math.min(w, h);
    const ox = z.logo.x0 + (w - cote) / 2, oy = z.logo.y0 + (h - cote) / 2, [a, , b, d] = lg.encre;
    // centré sur l'axe optique de la marque (ex. l'axe du T de Tesla), pas sur sa boîte englobante (retour de Thomas, 18/10/2026)
    const largeurMarque = ((b - a) / 1080) * cote, cx = ox + ((lg.axe ?? (a + b) / 2) / 1080) * cote, bas = oy + (d / 1080) * cote;
    const [ex0, ey0, ex1, ey1] = lm.encre, L = lm.rel.l * largeurMarque, H = (L * (ey1 - ey0)) / (ex1 - ex0);
    const y0 = bas + lm.rel.e * largeurMarque;
    // calé à droite de l'encre de la marque, comme le badge A2 sous les anneaux sur la photo (data-cale-marque="droite")
    if (lm.rel.c === 'droite') { const x1 = ox + (b / 1080) * cote; return { x0: x1 - L, x1, y0, y1: y0 + H }; }
    return { x0: cx - L / 2, x1: cx + L / 2, y0, y1: y0 + H };
  }

  nLogo(l, zone, couleur, ajuster) {
    const s = document.createElementNS(SVG, 'svg');
    let x = zone.x0, y = zone.y0, w = zone.x1 - zone.x0, h = zone.y1 - zone.y0, vb = '0 0 1080 1080';
    if (ajuster) {
      const [ex0, ey0, ex1, ey1] = l.encre, ew = ex1 - ex0, eh = ey1 - ey0;
      vb = `${ex0} ${ey0} ${ew} ${eh}`;
      const k = Math.min(w / ew, h / eh);
      x += (w - ew * k) / 2; w = ew * k; h = eh * k;   // calé en haut de la zone, juste sous la marque
    }
    s.setAttribute('viewBox', vb);
    [['x', x], ['y', y], ['width', w], ['height', h]].forEach(([a, val]) => s.setAttribute(a, val.toFixed(3)));
    s.setAttribute('class', 'gp-logo');
    s.setAttribute('preserveAspectRatio', 'xMidYMid meet');
    const p = document.createElementNS(SVG, 'path');
    p.setAttribute('d', l.d);
    if (l.tr) p.setAttribute('transform', l.tr);
    p.setAttribute('fill', couleur);
    s.appendChild(p);
    return s;
  }

  /** Texte gravé : capitales à la hauteur mesurée (18 pt DIN), centré, rétréci s'il dépasse la largeur utile. */
  nTexte(zone, texte, couleur, police = null) {
    const pol = police || POLICES.classique;
    let fs = zone.capitale / pol.cap;
    mesure.font = `${pol.w} 100px ${pol.f}`;
    const largeur100 = mesure.measureText(texte).width + pol.il * 100 * Math.max(0, [...texte].length - 1);
    const dispo = zone.x1 - zone.x0, larg = (largeur100 * fs) / 100;
    if (larg > dispo) fs *= dispo / larg;
    const t = document.createElementNS(SVG, 'text');
    t.setAttribute('x', zone.cx);
    t.setAttribute('y', zone.ligne);
    t.setAttribute('text-anchor', 'middle');
    t.setAttribute('font-size', fs.toFixed(3));
    t.setAttribute('fill', couleur);
    t.setAttribute('class', 'gp-texte');
    t.style.letterSpacing = `${pol.il}em`;
    if (police) { t.style.fontFamily = `'${pol.f}', serif`; t.style.fontWeight = pol.w; }
    t.textContent = texte;
    return t;
  }

  /** Plusieurs lignes (verso du porte-clés) : même taille pour toutes, réduite pour la plus longue et selon le nombre de lignes,
   *  bloc centré verticalement sur la ligne de base d'une ligne seule. */
  nTexteLignes(zone, lignes, couleur, police = null) {
    const pol = police || POLICES.classique;
    const n = lignes.length, cap = zone.capitale * (n >= 3 ? 0.8 : 0.9), pas = cap * 1.55;
    mesure.font = `${pol.w} 100px ${pol.f}`;
    const larg100 = Math.max(...lignes.map((t) => mesure.measureText(t).width + pol.il * 100 * Math.max(0, [...t].length - 1)));
    let fs = cap / pol.cap;
    const dispo = zone.x1 - zone.x0, larg = (larg100 * fs) / 100;
    if (larg > dispo) fs *= dispo / larg;
    const capReel = fs * pol.cap, pasReel = pas * (capReel / cap);
    const milieu = zone.ligne - zone.capitale / 2, premiere = milieu - ((n - 1) * pasReel) / 2 + capReel / 2;
    const g = document.createElementNS(SVG, 'g');
    lignes.forEach((t, i) => {
      const el = this.nTexte({ ...zone, capitale: capReel, ligne: premiere + i * pasReel }, t, couleur, police);
      el.setAttribute('font-size', fs.toFixed(3));
      g.appendChild(el);
    });
    g.setAttribute('class', 'gp-texte');
    return g;
  }

  /** Porte-clés assorti posé à côté du porte-carte (à l'échelle), dans son coloris, avec la même gravure : le logo de la pièce,
   *  dans le carré de 23 mm du porte-clés, à la teinte réelle mesurée pour ce coloris de porte-clés. */
  nPorteCles(lg, modele, zLogo) {
    const v = this.s.porte_cles_visuel, pc = this.d.supports.pc, e = this.etat;
    const assorti = this.d.supports.porte_cles_assorti.find((x) => x.v === e.porteCles);
    const coul = pc.coloris.find((x) => x.v === e.porteCles);
    if (!assorti?.detoure || !coul) return null;
    const g = document.createElementNS(SVG, 'g');
    g.setAttribute('class', 'gp-porte-cles');
    g.setAttribute('transform', `rotate(${v.rot} ${v.cx} ${v.cy})`);
    const s = document.createElementNS(SVG, 'svg');
    [['x', v.x], ['y', v.y], ['width', v.cote], ['height', v.cote]].forEach(([a, val]) => s.setAttribute(a, val));
    s.setAttribute('viewBox', '0 0 100 100');
    s.setAttribute('overflow', 'visible');
    const im = document.createElementNS(SVG, 'image');
    im.setAttribute('href', assorti.detoure);
    [['x', 0], ['y', 0], ['width', 100], ['height', 100]].forEach(([a, val]) => im.setAttribute(a, val));
    s.appendChild(im);
    if (lg && pc.zones.logo) {
      // même gravure que le porte-carte : marque + logo du modèle dessous (Thomas, 18/10/2026). La composition (carré de la
      // marque = 0–100, modèle placé comme sur le porte-carte) est réduite d'un bloc dans le carré du porte-clés.
      // logo agrandi de 12 % autour de son centre (retour de Victor du 20/10/2026 : trop petit sur le porte-clés assorti)
      const z0 = pc.zones.logo, gx = ((z0.x1 - z0.x0) * 0.12) / 2, gy = ((z0.y1 - z0.y0) * 0.12) / 2;
      const zp = { x0: z0.x0 - gx, y0: z0.y0 - gy, x1: z0.x1 + gx, y1: z0.y1 + gy }, cadre = document.createElementNS(SVG, 'svg');
      let vb = [0, 0, 100, 100];
      const enfants = [this.nLogo(lg, { x0: 0, y0: 0, x1: 100, y1: 100 }, coul.t.c, false)];
      if (modele && zLogo) {
        const w = zLogo.x1 - zLogo.x0, h = zLogo.y1 - zLogo.y0, cote = Math.min(w, h);
        const ox = zLogo.x0 + (w - cote) / 2, oy = zLogo.y0 + (h - cote) / 2, k = 100 / cote, m = modele.zone;
        const zm = { x0: (m.x0 - ox) * k, x1: (m.x1 - ox) * k, y0: (m.y0 - oy) * k, y1: (m.y1 - oy) * k };
        enfants.push(this.nLogo(modele.l, zm, coul.t.c, true));
        const x0 = Math.min(0, zm.x0), x1 = Math.max(100, zm.x1), y1 = Math.max(100, zm.y1);
        vb = [x0, 0, x1 - x0, y1];
      }
      cadre.setAttribute('viewBox', vb.map((v) => v.toFixed(3)).join(' '));
      cadre.setAttribute('preserveAspectRatio', 'xMidYMid meet');
      [['x', zp.x0], ['y', zp.y0], ['width', zp.x1 - zp.x0], ['height', zp.y1 - zp.y0]].forEach(([a, val]) => cadre.setAttribute(a, val));
      cadre.setAttribute('overflow', 'visible');
      cadre.style.mixBlendMode = coul.t.m;
      cadre.style.opacity = coul.t.o;
      cadre.append(...enfants);
      s.appendChild(cadre);
    }
    g.appendChild(s);
    return g;
  }

  /** Texte en bas mis en valeur : capitales 1,6 fois la taille réelle (rétrécies si besoin), gris très clair, dans un cadre
   *  pointillé sur la largeur utile de la couverture. Ce n'est pas le rendu de la gravure : la mention sous le champ le dit. */
  nTexteValeur(zone, texte) {
    const g = document.createElementNS(SVG, 'g');
    g.setAttribute('class', 'gp-atelier gp-texte-valeur');
    const cap = zone.capitale * 1.6, haut = cap * 2.3, milieu = zone.ligne - zone.capitale / 2;
    const r = document.createElementNS(SVG, 'rect');
    [['x', zone.x0], ['y', milieu - haut / 2], ['width', zone.x1 - zone.x0], ['height', haut], ['rx', 1.2]].forEach(([a, v]) => r.setAttribute(a, v.toFixed(3)));
    const t = this.nTexte({ ...zone, capitale: cap, ligne: milieu + cap / 2, x0: zone.x0 + 1.5, x1: zone.x1 - 1.5 }, texte, '#ECEDEE');
    g.append(r, t);
    return g;
  }

  /** Texte centré dans une zone (modèle gravé en texte d'une fiche modèle) : capitales à 45 % de la hauteur de la zone. */
  nTexteZone(zone, texte, couleur, capitale) {
    const h = zone.y1 - zone.y0;
    // taille du texte standard de l'atelier (18 pt DIN), calé en haut de la zone, sous la marque
    if (capitale) return this.nTexte({ cx: (zone.x0 + zone.x1) / 2, ligne: zone.y0 + capitale, capitale, x0: zone.x0, x1: zone.x1 }, texte, couleur);
    return this.nTexte({ cx: (zone.x0 + zone.x1) / 2, ligne: zone.y0 + h * 0.5 + h * 0.225, capitale: h * 0.45, x0: zone.x0, x1: zone.x1 }, texte, couleur);
  }

  /** Pas de fichier atelier (marque rare, consigne libre) : zone pointillée et nom, sans imiter une gravure (H2, H3). */
  nAtelier(zone, nom) {
    const g = document.createElementNS(SVG, 'g');
    g.setAttribute('class', 'gp-atelier');
    const r = document.createElementNS(SVG, 'rect');
    [['x', zone.x0], ['y', zone.y0], ['width', zone.x1 - zone.x0], ['height', zone.y1 - zone.y0], ['rx', 1.2]].forEach(([a, v]) => r.setAttribute(a, v));
    const t = document.createElementNS(SVG, 'text');
    t.setAttribute('x', (zone.x0 + zone.x1) / 2);
    t.setAttribute('y', (zone.y0 + zone.y1) / 2);
    t.setAttribute('text-anchor', 'middle');
    t.setAttribute('dominant-baseline', 'middle');
    const h = zone.y1 - zone.y0, w = zone.x1 - zone.x0;
    t.setAttribute('font-size', Math.min(h * 0.32, (w * 1.6) / Math.max(4, nom.length)).toFixed(2));
    t.textContent = nom;
    g.append(r, t);
    return g;
  }

  /** Nom en haut de l'image, comme sur les maquettes de la boutique. */
  nTitre(zone) {
    const f = this.d.fiche, e = this.etat;
    let nom = e.marque === (f.marque || '') && f.titre ? f.titre : nomAffiche(e.marque);
    // modèle choisi dans la grille : « TESLA MODEL Y », comme le titre des fiches modèle (Thomas, 18/10/2026)
    if (e.modeleOn && e.modele && e.logoModele && !e.modeleLibre && !nom.toLowerCase().includes(e.modele.toLowerCase())) nom += ` ${e.modele}`;
    nom = (nom || '').toLocaleUpperCase('fr');
    // police des titres du thème ; réduite si le titre dépasse 92 % de la largeur de l'image
    let fs = zone.capitale / 0.72;
    const racine = getComputedStyle(document.documentElement);
    mesure.font = `${racine.getPropertyValue('--font-heading--weight').trim() || 600} 100px ${racine.getPropertyValue('--font-heading--family').trim() || 'sans-serif'}`;
    const larg = ((mesure.measureText(nom).width + 2 * nom.length) * fs) / 100;   // + interlettrage 0,02 em
    if (larg > 92) fs *= 92 / larg;
    const t = document.createElementNS(SVG, 'text');
    t.setAttribute('x', zone.cx);
    t.setAttribute('y', zone.ligne);
    t.setAttribute('text-anchor', 'middle');
    t.setAttribute('font-size', fs.toFixed(3));
    t.setAttribute('class', 'gp-titre-image');
    t.textContent = nom;
    return t;
  }
}

/* ------------------------------------------------------------------ Laser (décor, repris de Mino en plus simple) */
function laser(hote, cible, mode) {
  if (reduit.matches || !hote.isConnected || !cible) return;
  let n = hote.querySelector(':scope > .gp-laser');
  if (!n) {
    n = document.createElement('div');
    n.className = 'gp-laser';
    n.setAttribute('aria-hidden', 'true');
    n.innerHTML = '<div class="gp-laser__point"></div>';
    hote.appendChild(n);
  }
  let b;
  try { b = cible.getBoundingClientRect(); } catch (e) { return; }
  const r = hote.getBoundingClientRect();
  if (!b.width || !r.width) return;
  const x0 = b.left - r.left, y0 = b.top - r.top, w = b.width, h = b.height;
  const point = n.firstChild;
  clearTimeout(n.minuterie);
  n.anim?.cancel();
  n.classList.add('is-allume');
  if (mode === 'frappe') {
    point.style.transform = `translate(${x0 + w}px, ${y0 + h * 0.5}px)`;
    n.anim = point.animate([{ opacity: 1, scale: 1.3 }, { opacity: 0.6, scale: 0.9 }, { opacity: 1, scale: 1 }], { duration: 160 });
    n.minuterie = setTimeout(() => n.classList.remove('is-allume'), 480);
  } else {
    // balayage ligne à ligne, comme la tête du laser sur une image tramée
    const lignes = 4, pas = [];
    for (let i = 0; i <= lignes; i++) {
      const y = y0 + (h * i) / lignes, aller = i % 2 === 0;
      pas.push({ transform: `translate(${aller ? x0 : x0 + w}px, ${y}px)` }, { transform: `translate(${aller ? x0 + w : x0}px, ${y}px)` });
    }
    n.anim = point.animate(pas, { duration: 720, easing: 'linear' });
    n.minuterie = setTimeout(() => n.classList.remove('is-allume'), 760);
  }
}

/* ------------------------------------------------------------------ Configurateur (élément <guillot-perso>) */
class GuillotPerso extends HTMLElement {
  connectedCallback() {
    if (this.pret) return;
    this.pret = true;
    // Pièce d'un coffret (<guillot-coffret>) : données communes lues dans le coffret, réglages propres à la pièce
    this.coffretEl = this.closest('guillot-coffret');
    if (this.coffretEl) {
      const c = this.coffretEl;
      c.donneesBrutes ||= JSON.parse(c.querySelector(':scope > [data-gp-coffret-donnees]').textContent);
      this.idx = Number(this.dataset.gpPiece);
      this.d = { ...c.donneesBrutes, t: { ...c.donneesBrutes.t }, valeurs: { ...c.donneesBrutes.valeurs }, fiche: c.donneesBrutes.pieces[this.idx], logo_fiche: null, logo_modele_fiche: null };
    } else this.d = JSON.parse(this.querySelector('[data-gp-donnees]').textContent);
    // le filtre « t » de Liquid échappe le HTML (d&#39;un) : textes décodés une fois, puis toujours posés en texte
    const dec = document.createElement('textarea');
    const decoder = (o) => Object.keys(o || {}).forEach((k) => { dec.innerHTML = o[k]; o[k] = dec.value; });
    decoder(this.d.t);
    decoder(this.d.valeurs);
    this.t = this.d.t;
    this.piece = new Piece(this.d);
    this.section = this.closest('.shopify-section') || document;
    this.fiche = this.d.fiche;
    this.cleStockage = `gp:${this.d.produit.handle}${this.coffretEl ? `:${this.idx}` : ''}`;
    this.monnaie = new Intl.NumberFormat(this.d.langue || 'fr', { style: 'currency', currency: this.d.devise || 'EUR' });
    this.restaurer();
    this.lier();
    this.afficherPrix();
    this.porteCles();
    this.marqueChoisie();
    this.synchroniserChamps();
    this.planifier(true);
    this.miniFlottante();
    window.addEventListener('pageshow', (ev) => ev.persisted && this.planifier(true));
    document.fonts?.load(POLICE).then(() => this.planifier(true), () => {});
  }

  $(s) { return this.querySelector(s); }
  $$(s) { return [...this.querySelectorAll(s)]; }
  tr(k, rep = {}) { return Object.entries(rep).reduce((s, [a, b]) => s.replaceAll(`[${a}]`, b), this.t[k] || k); }
  prix(cents) { return this.monnaie.format(cents / 100); }
  cit(s) { const q = { de: ['„', '“'], en: ['“', '”'] }[(this.d.langue || 'fr').slice(0, 2)] || ['« ', ' »']; return `${q[0]}${s}${q[1]}`; }

  /* ---------- état : localStorage (reprise après rechargement ou retour arrière), effacé après l'ajout au panier */
  restaurer() {
    try {
      const s = JSON.parse(localStorage.getItem(this.cleStockage) || 'null');
      if (s && s.v === 1) Object.assign(this.piece.etat, s.etat, { face: 'recto' });
      // réglages de la fiche toujours repris de la fiche (taille, logo corrigé depuis la dernière visite)
      this.piece.etat.mm = this.fiche.taille_mm || null;
      if (this.fiche.logo && this.piece.etat.marque === (this.fiche.marque || '') && !this.piece.etat.variante) {
        this.piece.etat.logo = this.fiche.logo;
        this.piece.etat.logoUrl = this.d.logo_fiche;
      }
      // fiche d'un modèle (Tesla Model Y) ou d'une finition dont le logo est déjà gravé (Pack M) : pas d'option de modèle
      if (this.fiche.modele || this.fiche.modele_fixe) this.piece.etat.modeleOn = false;
      if (s?.qte && this.$('[data-gp-qte-champ]')) this.$('[data-gp-qte-champ]').value = s.qte;
      if (s?.etat?.marque && s.etat.marque !== (this.fiche.marque || '')) this.chargerMarques();
    } catch (e) { /* stockage indisponible : on part de la fiche */ }
  }

  sauver() {
    try {
      localStorage.setItem(this.cleStockage, JSON.stringify({ v: 1, etat: this.piece.etat, qte: this.qte() }));
    } catch (e) { /* navigation privée */ }
  }

  synchroniserChamps() {
    const e = this.piece.etat;
    this.$$('[data-gp-coloris]').forEach((r) => (r.checked = r.value === e.coloris));
    ['haut', 'bas'].forEach((k) => { const ch = this.$(`[data-gp-champ="${k}"]`); if (ch) ch.value = e[k] || ''; });
    [['modele', 'modeleOn'], ['texte', 'texteOn'], ['verso', 'versoOn'], ['recto', 'rectoOn']].forEach(([k, on]) => {
      const c = this.$(`[data-gp-option="${k}"]`), ch = this.$(`[data-gp-champ="${k}"]`), b = this.$(`[data-gp-bloc="${k}"]`);
      if (!c) return;
      c.checked = e[on];
      b.hidden = !e[on];
      ch.value = e[k] || '';
    });
    this.$$('[data-gp-porte-cles] input').forEach((r) => (r.checked = r.value === (e.porteCles || '')));
    this.$$('[data-gp-police]').forEach((r) => (r.checked = r.value === (e.police || 'classique')));
    if (e.police && e.police !== 'classique') { const pol = POLICES[e.police]; document.fonts?.load(`${pol.w} 100px ${pol.f}`).then(() => this.planifier(true), () => {}); }
    const intro = this.$('[data-gp-modele-introuvable]');
    if (intro) { intro.checked = e.modeleLibre; this.$('[data-gp-bloc="modele-libre"]').hidden = !e.modeleLibre; }
    if (e.modeleOn) this.remplirModeles();
  }

  /* ---------- événements */
  lier() {
    this.addEventListener('change', (ev) => {
      const el = ev.target, e = this.piece.etat;
      if (el.matches('[data-gp-coloris]')) { e.coloris = el.value; this.changement('coloris'); }
      else if (el.matches('[data-gp-option]')) {
        const k = el.dataset.gpOption;
        e[`${k}On`] = el.checked;
        this.$(`[data-gp-bloc="${k}"]`).hidden = !el.checked;
        if (el.checked) setTimeout(() => this.$(`[data-gp-champ="${k}"]`)?.focus(), 0);
        if (k === 'verso') e.face = el.checked && e.verso ? 'verso' : 'recto';
        if (k === 'modele' && el.checked) this.remplirModeles();
        this.changement(k);
      } else if (el.matches('[data-gp-porte-cles] input')) { e.porteCles = el.value; this.changement('porte_cles'); }
      else if (el.matches('[data-gp-police]')) {
        e.police = el.value;
        if (e.verso) e.face = 'verso';
        const pol = POLICES[e.police];
        document.fonts?.load(`${pol.w} 100px ${pol.f}`).then(() => this.planifier(true), () => {});
        this.changement('verso');
      }
      else if (el.matches('[data-gp-modele-introuvable]')) this.modeleLibre(el.checked);
    });
    this.addEventListener('input', (ev) => {
      const el = ev.target;
      if (el.matches('[data-gp-champ]')) {
        const k = el.dataset.gpChamp, e = this.piece.etat, max = Number(el.dataset.gpLignes || 0);
        // champ sur plusieurs lignes (verso du porte-clés) : pas plus de lignes que la gravure n'en permet
        if (max && el.value.split('\n').length > max) el.value = el.value.split('\n').slice(0, max).join('\n');
        e[k] = el.value;
        if (k === 'verso') e.face = el.value.trim() ? 'verso' : 'recto';
        if (k === 'modele') { e.logoModele = null; e.modeleLibre = true; }
        this.changement(k, true);
      } else if (el.matches('[data-gp-recherche]')) this.filtrerMarques(el.value);
      else if (el.matches('[data-gp-recherche-modeles]')) {
        const q = normaliser(el.value);
        this.$$('[data-gp-modele-carte]').forEach((b) => { b.hidden = !!q && !normaliser(b.dataset.gpModeleCarte).includes(q); });
      }
      else if (el.matches('[data-gp-qte-champ]')) { this.afficherPrix(); this.sauver(); }
    });
    this.addEventListener('click', (ev) => {
      const b = ev.target.closest('button');
      if (!b || !this.contains(b)) return;
      if (b.matches('[data-gp-marque-changer]')) this.ouvrirMarques(b);
      else if (b.matches('[data-gp-toutes]')) this.filtrerMarques(this.$('[data-gp-recherche]').value, true);
      else if (b.matches('[data-gp-marque]')) this.choisirMarque(b.dataset.gpMarque);
      else if (b.matches('[data-gp-modele-carte]')) this.choisirModele(b.dataset.gpModeleCarte, b.dataset.slug);
      else if (b.matches('[data-gp-variante]')) this.choisirVariante(b.dataset.gpVariante, b.dataset.titre);
      else if (b.matches('[data-gp-qte]')) this.changerQte(Number(b.dataset.gpQte));
      else if (b.matches('[data-gp-ajouter]')) this.ajouter();
      else if (b.matches('[data-gp-zoom]')) this.zoom();
      else if (b.matches('[data-gp-fermer]')) this.$('[data-gp-dialogue]').close();
      else if (b.matches('[data-gp-face]')) { this.piece.etat.face = b.dataset.gpFace; this.planifier(); }
    });
    this.addEventListener('keydown', (ev) => {
      if (ev.target.matches('[data-gp-recherche]')) this.claviertListe(ev, '[data-gp-grille]');
    });
    // Galerie : l'image principale reconstruite par le thème garde l'aperçu ; clic sur l'aperçu → zoom de l'aperçu
    this.galerie = this.section.querySelector('product-media-gallery');
    if (this.galerie && this.fiche.apercu !== false) {
      new MutationObserver(() => this.planifier()).observe(this.galerie, { childList: true, subtree: true });
      this.galerie.addEventListener('click', (ev) => {
        if (ev.target.closest('.gp-calque') && this.$('[data-gp-dialogue]')) { ev.preventDefault(); ev.stopPropagation(); this.zoom(); }
      }, true);
    }
  }

  changement(quoi, frappe = false) {
    this.dernier = { quoi, frappe };
    if (this.coffretEl) this.coffretEl.dernierePiece = this.idx;
    this.validerChamp(quoi);
    this.afficherPrix();
    this.recap();
    this.sauver();
    if (!frappe && quoi !== 'porte_cles' && this.fiche.apercu !== false) this.premiereImage();
    this.planifier();
  }

  /** Mobile : la copie de l'aperçu reste visible sous l'en-tête pendant la saisie. Le thème met « overflow: auto » sur html et
   *  body, ce qui neutralise position: sticky : la copie passe donc en position fixe tant que le configurateur est à l'écran,
   *  une réserve de même hauteur évite tout saut de mise en page. */
  miniFlottante() {
    const mini = this.$('[data-gp-mini]');
    if (this.fiche.apercu === false || !mini) return;
    const mobile = matchMedia('(max-width: 749px)');
    const reserve = document.createElement('div');
    reserve.className = 'gp-mini-reserve';
    mini.before(reserve);
    const entete = document.querySelector('header-component, .header, header');
    const maj = () => {
      if (!mobile.matches) { mini.classList.remove('is-flottante'); reserve.style.height = '0px'; return; }
      const haut = Math.max(0, entete && getComputedStyle(entete).position !== 'static' ? entete.getBoundingClientRect().bottom : 0) + 6;
      const r = this.getBoundingClientRect(), h = mini.offsetHeight;
      const flotte = r.top < haut && r.bottom > haut + h + 60;
      if (flotte !== mini.classList.contains('is-flottante')) {
        reserve.style.height = flotte ? `${h + 14}px` : '0px';
        mini.classList.toggle('is-flottante', flotte);
      }
      if (flotte) { mini.style.top = `${haut}px`; mini.style.left = `${r.left}px`; mini.style.width = `${r.width}px`; }
      else mini.style.removeProperty('top');
    };
    addEventListener('scroll', maj, { passive: true });
    addEventListener('resize', maj, { passive: true });
    maj();
  }

  /* ---------- aperçu : galerie (1re image), copie collante sur mobile, zoom */
  planifier(force = false) {
    if (force) this.force = true;
    clearTimeout(this.minuterie);
    this.minuterie = setTimeout(() => this.appliquer(), 30);   // pas de requestAnimationFrame : suspendu en arrière-plan
  }

  cadresGalerie() {
    if (!this.galerie) return [];
    const id = this.dataset.media;
    let li = [...this.galerie.querySelectorAll(`[data-zoom-media-id="${id}"]`)];
    if (!li.length) li = [this.galerie.querySelector('.product-media-gallery__media')].filter(Boolean);
    return li.map((x) => x.querySelector('.product-media')).filter(Boolean);
  }

  async appliquer() {
    const p = this.piece, cle = p.cle(), force = this.force;
    this.force = false;
    const sel = this.coffretEl ? `:scope > .gp-calque[data-gp-piece="${this.idx}"]` : ':scope > .gp-calque';
    // fiche sans aperçu (H39), ou pièce d'un coffret dont l'étape n'est pas ouverte : la photo de la fiche reste telle quelle
    if (this.fiche.apercu === false || (this.coffretEl && !this.actif)) {
      this.cadresGalerie().forEach((cadre) => cadre.querySelector(sel)?.remove());
      this.resume();
      return;
    }
    const photo = p.commeLaPhoto();
    const hotes = [];
    for (const cadre of this.cadresGalerie()) {
      let calque = cadre.querySelector(sel);
      if (photo) { calque?.remove(); continue; }
      if (!calque) {
        calque = document.createElement('div');
        calque.className = 'gp-calque gp-hote';
        if (this.coffretEl) calque.dataset.gpPiece = this.idx;
        cadre.appendChild(calque);
      }
      if (calque.dataset.gpCle !== cle || force) hotes.push([calque, 'img']);
    }
    const mini = this.$('[data-gp-hote="mini"]');
    if (mini && (mini.dataset.gpCle !== cle || force)) hotes.push([mini, 'mini']);
    const dlg = this.$('[data-gp-dialogue]');
    if (dlg?.open) hotes.push([this.$('[data-gp-hote="zoom"]'), 'img']);
    const rendus = await Promise.all(hotes.map(([h, t]) => p.dessiner(h, { taille: t }).then((r) => [h, r])));
    const faces = this.$('[data-gp-faces]');
    if (faces) faces.hidden = !(this.piece.etat.versoOn && this.piece.etat.verso);
    this.$$('[data-gp-face]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.gpFace === p.etat.face)));
    this.resume();
    // laser sur la zone modifiée (décor)
    const dern = this.dernier;
    this.dernier = null;
    if (dern) {
      rendus.forEach(([h, r]) => {
        const cible = ['texte', 'verso', 'recto'].includes(dern.quoi) ? (r.grav.querySelector('.gp-texte') || r.deco.querySelector('.gp-texte'))
          : dern.quoi === 'modele' ? (r.grav.querySelectorAll('.gp-logo')[1] || r.deco.querySelector('.gp-atelier'))
          : dern.quoi === 'marque' ? (r.grav.querySelector('.gp-logo') || r.deco.querySelector('.gp-atelier')) : null;
        if (cible) laser(h, cible, dern.frappe ? 'frappe' : 'balayage');
      });
    }
    this.precharger();
  }

  precharger() {
    if (this.precharge) return;
    this.precharge = true;
    setTimeout(() => this.piece.s.coloris.forEach((c) => { const i = new Image(); i.src = c.img; const m = new Image(); m.src = c.mini; }), 1200);
  }

  premiereImage() {
    // mobile (carrousel) : retour sur l'image principale, qui porte l'aperçu
    const s = this.galerie?.querySelector('.splide');
    try { (s?.splide || this.galerie?.mainSlider || this.galerie?.splide)?.go?.(0); } catch (e) { /* galerie sans API */ }
  }

  zoom() {
    const dlg = this.$('[data-gp-dialogue]');
    if (!dlg.open) dlg.showModal();
    this.planifier(true);
  }

  resume() {
    const e = this.piece.etat, parts = [this.valeur(e.coloris)];
    if (e.marque) parts.push(nomAffiche(e.marque));
    if (e.modeleOn && e.modele) parts.push(e.modele);
    if (e.texteOn && e.texte) parts.push(this.cit(e.texte));
    if (e.versoOn && e.verso) parts.push(`${this.t.verso_vue} ${this.cit(e.verso)}`);
    if (this.piece.rectoTexte()) parts.push(this.cit(this.piece.rectoTexte()));
    const txt = parts.join(' · ');
    const res = this.$('[data-gp-resume]');
    if (res) res.textContent = txt;
    const cn = this.$('[data-gp-coloris-nom]');
    if (cn) cn.textContent = this.valeur(e.coloris);
    const a = this.$('[data-gp-annonce]') || this.coffretEl?.querySelector('[data-gp-annonce]');
    if (!a) return;
    clearTimeout(this.minAnnonce);
    this.minAnnonce = setTimeout(() => (a.textContent = `${this.t.apercu_maj}${this.d.langue?.startsWith('fr') ? ' :' : ':'} ${txt}`), 700);
  }

  valeur(v) { return this.d.valeurs?.[v] && !this.d.valeurs[v].includes('translation missing') ? this.d.valeurs[v] : v; }

  /* ---------- prix et récapitulatif */
  qte() {
    const champ = this.$('[data-gp-qte-champ]') || this.coffretEl?.querySelector('[data-gp-qte-champ]');
    return Math.min(99, Math.max(1, parseInt(champ?.value, 10) || 1));
  }

  changerQte(delta) {
    this.$('[data-gp-qte-champ]').value = Math.min(99, Math.max(1, this.qte() + delta));
    this.afficherPrix();
    this.sauver();
  }

  afficherPrix() {
    const op = this.d.options, cfg = this.fiche.config;
    const prixOption = { logo_supp: op.logo_supp.prix, texte_bas: this.piece.optionTexte().prix, verso: this.piece.optionVerso().prix, porte_clefs: op.porte_clefs.variantes.Noir?.prix };
    this.$$('[data-gp-prix]').forEach((el) => { const v = prixOption[el.dataset.gpPrix]; el.textContent = v ? `+${this.prix(v)}` : ''; });
    if (this.coffretEl) { this.coffretEl.maj?.(); return; }   // total du coffret : toutes les pièces
    const unit = this.d.produit.prix + this.piece.options().reduce((s, o) => s + o.prix, 0);
    this.$('[data-gp-total]').textContent = this.prix(unit * this.qte());
  }

  recap() {
    if (this.coffretEl) { this.coffretEl.maj?.(); return; }
    this.$('[data-gp-recap-liste]').innerHTML = this.lignesRecap().join('');
  }

  lignesRecap() {
    const e = this.piece.etat, li = [];
    const ajoute = (k, v, prix) => li.push(`<li><span>${k}</span><span>${v}${prix ? ` <em>+${this.prix(prix)}</em>` : ''}</span></li>`);
    const esc = (s) => s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);
    if (this.fiche.support === 'da') {
      if ((e.haut || '').trim()) ajoute(this.t.da_haut, this.cit(esc(e.haut.trim().toLocaleUpperCase('fr'))));
      if ((e.bas || '').trim()) ajoute(this.t.da_bas, this.cit(esc(e.bas.trim().toLocaleUpperCase('fr'))));
      return li;
    }
    ajoute(this.t[`coloris_${this.fiche.support}`], esc(this.valeur(e.coloris)));
    if (this.fiche.sans_logo) { /* fiche sans gravure de logo (porte-carte grise Similicuir) */ }
    else if (this.piece.rectoTexte()) ajoute(this.t.recto, this.cit(esc(this.piece.rectoTexte())));
    else if (e.marque && this.fiche.modele_fixe && e.marque === this.fiche.marque) ajoute(this.t.logo_grave, esc(this.fiche.titre || nomAffiche(e.marque)));   // Pack M : « Logo gravé : M »
    else if (e.marque) ajoute(this.t.marque, esc(nomAffiche(e.marque)), e.marque === this.d.options.logo_par_mail.valeur ? this.d.options.logo_par_mail.prix : 0);
    if (e.varianteNom && !this.piece.rectoTexte()) ajoute(this.t.variante_court, esc(this.piece.varianteGravee()));
    if (this.fiche.modele) ajoute(this.t.modele, `${esc(this.fiche.modele)} (${this.t.inclus})`);
    if (e.modeleOn && e.modele) {
      if (this.piece.modeleInclus) ajoute(this.t.modele, `${esc(e.modele)} (${this.t.inclus})`);
      else ajoute(this.t.modele, esc(e.modele), this.d.options.logo_supp.prix);
    }
    if (e.texteOn && e.texte) ajoute(this.t.texte, this.cit(esc(e.texte)), this.piece.optionTexte().prix);
    if (e.versoOn && e.verso) ajoute(this.t.verso, `${this.cit(esc(e.verso))}${this.fiche.support === 'pc' && e.police !== 'classique' ? ` (${esc(this.t[`police_${e.police}`] || e.police)})` : ''}`, this.piece.optionVerso().prix);
    if (e.porteCles) ajoute(this.t.porte_cles, esc(this.valeur(e.porteCles)), this.d.options.porte_clefs.variantes[e.porteCles]?.prix);
    return li;
  }

  /* ---------- validation : message à l'endroit du champ, jamais de bouton bloqué sans explication */
  erreur(k, msg) {
    const el = this.$(`[data-gp-erreur="${k}"]`);
    if (!el) return;
    el.textContent = msg || '';
    el.hidden = !msg;
    const champ = this.$(`[data-gp-champ="${k}"]`);
    if (champ) { champ.setAttribute('aria-invalid', String(Boolean(msg))); if (msg) champ.setAttribute('aria-errormessage', el.id || ''); }
  }

  validerChamp(k) {
    if (!['texte', 'verso', 'modele', 'recto', 'haut', 'bas'].includes(k)) return true;
    const e = this.piece.etat, max = ['haut', 'bas'].includes(k) ? 18 : this.d.supports.limites[k] ?? this.d.supports.limites.verso;
    const on = ['haut', 'bas'].includes(k) ? true : e[`${k}On`];
    const compteur = this.$(`[data-gp-compteur="${k}"]`);
    if (!on) { this.erreur(k, ''); return true; }
    const v = verifierTexte(e[k], max);
    if (compteur) { compteur.textContent = `${v.n} / ${max}`; compteur.classList.toggle('is-trop', v.trop); }
    let msg = '';
    if (v.refus.length) msg = this.tr('erreur_caracteres', { car: v.refus.join(' ') });
    else if (v.trop) msg = this.tr('erreur_longueur', { max });
    this.erreur(k, msg);
    return !msg;
  }

  /** Pièce de coffret : valide sans rien afficher (le coffret ouvre l'étape fautive, puis appelle valider()). */
  estValide() {
    const e = this.piece.etat;
    if (!e.marque && !this.piece.rectoTexte()) return false;
    for (const k of ['modele', 'texte', 'verso', 'recto']) {
      if (!this.$(`[data-gp-option="${k}"]`) || !e[`${k}On`]) continue;
      if (!(e[k] || '').trim()) return false;
      const v = verifierTexte(e[k], this.d.supports.limites[k] ?? this.d.supports.limites.verso);
      if (v.refus.length || v.trop) return false;
    }
    return true;
  }

  valider() {
    const e = this.piece.etat;
    let premier = null;
    const ko = (k, msg, focus) => { this.erreur(k, msg); premier ||= focus; };
    if (this.fiche.support === 'da') {   // disque : au moins un des deux textes
      if (!(e.haut || '').trim() && !(e.bas || '').trim()) ko('haut', this.t.erreur_disque_texte, this.$('[data-gp-champ="haut"]'));
      for (const k of ['haut', 'bas']) if (!this.validerChamp(k)) premier ||= this.$(`[data-gp-champ="${k}"]`);
    } else if (!e.marque && !this.piece.rectoTexte()) ko('marque', this.t.erreur_marque, this.$('[data-gp-recherche]'));
    for (const k of ['modele', 'texte', 'verso', 'recto']) {
      if (!this.$(`[data-gp-option="${k}"]`)) continue;
      if (e[`${k}On`] && !e[k].trim()) ko(k, k === 'modele' ? this.t.erreur_modele : this.t.erreur_vide, k === 'modele' && !e.modeleLibre ? this.$('[data-gp-grille-modeles] button, [data-gp-modele-introuvable]') : this.$(`[data-gp-champ="${k}"]`));
      else if (!this.validerChamp(k)) premier ||= this.$(`[data-gp-champ="${k}"]`);
    }
    if (premier) {
      premier.scrollIntoView({ block: 'center', behavior: reduit.matches ? 'auto' : 'smooth' });
      setTimeout(() => premier.focus({ preventScroll: true }), 250);
      return false;
    }
    return true;
  }

  /* ---------- panier : mêmes propriétés et mêmes produits cachés que l'app (contrat), un seul envoi */
  async ajouter() {
    this.erreur('panier', '');
    if (!this.valider()) return;
    const bouton = this.$('[data-gp-ajouter]'), libelle = this.$('[data-gp-ajouter-texte]');
    const p = this.piece, qte = this.qte();
    // Nettoyage des textes (espaces) au moment de l'envoi
    ['texte', 'verso', 'modele'].forEach((k) => (p.etat[k] = nettoyer(p.etat[k], k === 'verso' && p.d.fiche.support === 'pc' ? 3 : 1)));
    const props = p.proprietes(), options = p.options();
    const id = String(Date.now());
    if (options.length) props._apo_order = id;
    const items = [{ id: this.d.produit.variante, quantity: qte, properties: props }];
    options.forEach((o) => items.push({ id: o.id, quantity: qte, properties: { _apo_option: o.apo, _apo_order: `Added by: ${id} * 1` } }));
    const sections = ['auto-gifts'];
    const tiroir = document.querySelector('cart-drawer');
    if (tiroir?.dataset.sectionId) sections.push(tiroir.dataset.sectionId);
    bouton.disabled = true;
    bouton.classList.add('is-loading');
    libelle.textContent = this.t.ajout_en_cours;
    try {
      // fichier de gravure du support (et du porte-clés assorti) joint à la ligne principale (Thomas, 22/10/2026)
      const base = `gravure-${this.d.produit.handle}`;
      const fichiers = await fichiersPiece(p, '_Fichier gravure', base);
      if (p.etat.porteCles && this.fiche.support === 'pcg') {
        const pcf = { ...this.d.fiche, support: 'pc', config: 'pc-perso', logo_cadre: null };
        const pc = new Piece({ ...this.d, fiche: pcf });
        Object.assign(pc.etat, { ...p.etat, coloris: 'Blanc', texteOn: false, texte: '', versoOn: false, verso: '', porteCles: '', mm: null });
        fichiers.push(...(await fichiersPiece(pc, '_Fichier gravure porte-clés', `${base}-porte-cles`)));
      }
      const r = await envoyerPanier(this.d.ajout, items, sections.join(','), fichiers);
      const rep = await r.json();
      if (!r.ok || rep.status) throw new Error(rep.description || rep.message || r.status);
      if (options.length) window.guillotPanier?.enregistrer(id, options);   // cohérence du panier (assets/guillot-panier.js)
      const compteur = document.querySelector('cart-icon')?.getCurrentItemCount?.();
      document.dispatchEvent(new CartUpdatedEvent({}, {
        itemCount: compteur != null ? compteur + items.reduce((s, i) => s + i.quantity, 0) : undefined,
        source: 'guillot-perso', sections: rep.sections,
      }));
      libelle.textContent = this.t.ajoute;
      try { localStorage.removeItem(this.cleStockage); } catch (e) { /* rien */ }
      setTimeout(() => (libelle.textContent = this.t.ajouter), 2500);
    } catch (err) {
      this.erreur('panier', `${this.t.erreur_panier}${err?.message && !/^\d+$/.test(err.message) ? ` (${err.message})` : ''}`);
      libelle.textContent = this.t.ajouter;
    } finally {
      bouton.disabled = false;
      bouton.classList.remove('is-loading');
    }
  }

  /* ---------- marque : affichage, recherche instantanée, grille de logos */
  base() { return this.d.marques.replace(/guillot-marques\.json.*$/, ''); }
  urlLogo(slug) { return slug ? `${this.base()}gl-${slug}.svg?v=${this.versionMarques || 1}` : null; }

  async chargerMarques() {
    if (!this.listeMarques) {
      const j = await charger(this.d.marques);
      this.versionMarques = j.h || j.v;
      const pc = this.fiche.config === 'pc-perso' && !this.coffretEl;   // liste Aris du porte-clé voiture personnalisé (sans motos)
      this.listeMarques = j.marques.filter((m) => !pc || m.pc).map((m) => ({ ...m, cle: normaliser(m.n), alias: (m.a || []).map(normaliser) }));
    }
    return this.listeMarques;
  }

  async marqueChoisie() {
    const e = this.piece.etat;
    if (this.fiche.support === 'da') { this.recap(); this.planifier(); return; }   // disque : pas de marque
    if (e.marque && e.marque !== (this.fiche.marque || '')) {
      const liste = await this.chargerMarques();
      const m = liste.find((x) => x.n === e.marque), v = e.variante && m?.v?.find((x) => x.l === e.logo);
      e.logo = v ? v.l : m?.l || null;
      e.logoUrl = this.urlLogo(e.logo);
      e.mm = v ? v.mm || null : m?.mm || null;
    }
    this.remplirVariantes();
    const nom = e.marque === (this.fiche.marque || '') ? (this.fiche.titre || nomAffiche(e.marque)) : nomAffiche(e.marque);
    const nm = this.$('[data-gp-marque-nom]');
    if (nm) nm.textContent = nom || '—';
    const ic = this.$('[data-gp-marque-logo]');
    if (ic) ic.innerHTML = e.logoUrl ? `<img src="${e.logoUrl}" alt="" width="40" height="40" loading="lazy">` : '';
    if (e.marque && this.fiche.config.includes('perso')) this.$('[data-gp-marque-choisie]').hidden = false;
    if (this.fiche.config.includes('perso') && !this.grilleRemplie) this.filtrerMarques('');
    this.recap();
    this.planifier();
  }

  ouvrirMarques(bouton) {
    const choix = this.$('[data-gp-choix-marque]'), ouvert = !choix.hidden;
    choix.hidden = ouvert;
    bouton.setAttribute('aria-expanded', String(!ouvert));
    if (!ouvert) { this.filtrerMarques(''); setTimeout(() => this.$('[data-gp-recherche]').focus(), 0); }
  }

  async filtrerMarques(q, toutes = false) {
    const liste = await this.chargerMarques();
    this.grilleRemplie = true;
    const n = normaliser(q);
    let res;
    if (n) {
      const score = (m) => (m.cle.startsWith(n) || m.alias.some((a) => a.startsWith(n)) ? 0 : m.cle.includes(n) || m.alias.some((a) => a.includes(n)) ? 1 : 9);
      res = liste.map((m) => [score(m), m]).filter(([s]) => s < 9).sort((a, b) => a[0] - b[0] || (a[1].r || 999) - (b[1].r || 999)).map(([, m]) => m);
    } else res = toutes ? [...liste].sort((a, b) => a.cle.localeCompare(b.cle)) : liste.filter((m) => m.r).slice(0, 12);
    this.$('[data-gp-grille-titre]').textContent = n || toutes ? this.tr('marque_resultats', { n: res.length }) : this.t.marque_populaires;
    const g = this.$('[data-gp-grille]');
    const e = this.piece.etat;
    const vus = res.slice(0, toutes || n ? 200 : 12), compte = {};
    vus.forEach((m) => { const k = nomAffiche(m.n); compte[k] = (compte[k] || 0) + 1; });
    g.innerHTML = res.length ? vus.map((m) => {
      const mail = m.n === this.d.options.logo_par_mail.valeur;
      // deux marques au même nom affiché (Honda auto / Honda moto) : on garde la précision entre parenthèses
      const nom = mail ? this.t.marque_logo_mail : compte[nomAffiche(m.n)] > 1 ? m.n.replace(/\s*\((.*?)\)\s*/, ' $1').trim() : nomAffiche(m.n);
      const visu = m.l ? `<img src="${this.urlLogo(m.l)}" alt="" loading="lazy" width="56" height="56">` : `<span class="gp-carte__sigle">${mail ? '@' : nom.slice(0, 2)}</span>`;
      return `<button type="button" class="gp-carte" role="option" aria-selected="${m.n === e.marque}" data-gp-marque="${m.n.replace(/"/g, '&quot;')}">${visu}<span>${nom}${mail ? ` <em>+${this.prix(this.d.options.logo_par_mail.prix)}</em>` : ''}</span></button>`;
    }).join('') : `<p class="gp-petit">${this.t.marque_aucune}</p>`;
    this.$('[data-gp-recherche]').setAttribute('aria-expanded', String(res.length > 0));
  }

  async choisirMarque(nom) {
    const e = this.piece.etat;
    if (nom !== e.marque) {
      e.variante = false;   // nouvelle marque : son logo par défaut
      e.varianteNom = '';
      // le modèle saisi appartenait à l'ancienne marque : on l'efface (sinon « Z900 » resterait sur une Peugeot, facturé +3 €)
      e.modele = '';
      e.logoModele = null;
      e.modeleLibre = false;
      const champ = this.$('[data-gp-champ="modele"]');
      if (champ) champ.value = '';
      const intro = this.$('[data-gp-modele-introuvable]');
      if (intro) intro.checked = false;
      const libre = this.$('[data-gp-bloc="modele-libre"]');
      if (libre) libre.hidden = true;
    }
    e.marque = nom;
    if (nom === (this.fiche.marque || '')) { e.logo = this.fiche.logo || null; e.logoUrl = this.d.logo_fiche; e.mm = this.fiche.taille_mm || null; }
    this.erreur('marque', '');
    this.$$('[data-gp-marque]').forEach((b) => b.setAttribute('aria-selected', String(b.dataset.gpMarque === nom)));
    await this.marqueChoisie();
    if (!this.fiche.config.includes('perso')) {
      this.$('[data-gp-choix-marque]').hidden = true;
      this.$('[data-gp-marque-changer]').setAttribute('aria-expanded', 'false');
    }
    if (e.modeleOn) this.remplirModeles();
    this.changement('marque');
  }

  /* ---------- variantes du logo de la marque (retour de Thomas du 21/10/2026) */
  async remplirVariantes() {
    const etape = this.$('[data-gp-etape="variante"]');
    if (!etape) return;
    const e = this.piece.etat, f = this.fiche;
    let v = [];
    if (e.marque && !f.modele_fixe && !f.sans_logo && f.apercu !== false) {
      try { v = (await this.chargerMarques()).find((x) => x.n === e.marque)?.v || []; } catch (err) { /* index indisponible */ }
    }
    // le logo de la fiche doit faire partie des variantes (sinon c'est un logo particulier : pas de choix)
    if (!v.some((x) => x.l === e.logo)) v = [];
    e.varianteNom = v.find((x) => x.l === e.logo)?.t || '';
    etape.hidden = v.length < 2 || !!this.coffretEl?.meme?.[this.idx];
    this.$('[data-gp-variantes]').innerHTML = v.map((x) => `<button type="button" class="gp-carte" role="option" aria-selected="${x.l === e.logo}" data-gp-variante="${x.l}" data-titre="${x.t.replace(/"/g, '&quot;')}"><img src="${this.urlLogo(x.l)}" alt="" loading="lazy" width="56" height="56"><span>${x.t}</span></button>`).join('');
  }

  choisirVariante(slug, titre) {
    const e = this.piece.etat, f = this.fiche;
    this.chargerMarques().then((liste) => {
      const v = liste.find((x) => x.n === e.marque)?.v?.find((x) => x.l === slug);
      e.logo = slug;
      e.logoUrl = slug === f.logo && e.marque === (f.marque || '') && this.d.logo_fiche ? this.d.logo_fiche : this.urlLogo(slug);
      e.mm = slug === f.logo ? f.taille_mm || null : v?.mm || null;
      e.variante = true;
      e.varianteNom = titre;
      this.$$('[data-gp-variante]').forEach((b) => b.setAttribute('aria-selected', String(b.dataset.gpVariante === slug)));
      const ic = this.$('[data-gp-marque-logo]');
      if (ic) ic.innerHTML = `<img src="${e.logoUrl}" alt="" width="40" height="40" loading="lazy">`;
      this.changement('marque');
    });
  }

  /** Flèches haut / bas dans une grille de cartes (recherche de marque). */
  claviertListe(ev, sel) {
    const items = this.$$(`${sel} button`);
    if (!items.length) return;
    const i = items.indexOf(document.activeElement);
    if (ev.key === 'ArrowDown') { ev.preventDefault(); items[Math.min(items.length - 1, i + 1)].focus(); }
    else if (ev.key === 'ArrowUp' && i > 0) { ev.preventDefault(); items[i - 1].focus(); }
  }

  /* ---------- modèle : grille des logos de modèle prêts pour la marque, sinon description libre (Thomas, 17/10/2026) */
  async remplirModeles() {
    const grille = this.$('[data-gp-grille-modeles]');
    if (!grille) return;
    const e = this.piece.etat;
    let liste = [];
    try {
      const j = await charger(this.d.modeles);
      if (j.h) this.versionMarques = j.h;   // même empreinte que l'index des marques : adresses des SVG à jour
      liste = j.m?.[e.marque] || [];
    } catch (err) { /* index indisponible : description libre */ }
    const aucun = !liste.length;
    grille.hidden = aucun;
    this.$('[data-gp-modeles-titre]').textContent = aucun ? this.t.modele_aucun : this.t.modele_choisir;
    this.$('[data-gp-introuvable]').hidden = aucun;
    // beaucoup de modèles (BMW, Audi…) : champ de recherche au-dessus de la grille ; ordre naturel (316i avant 1000)
    liste = [...liste].sort((a, b) => a.n.localeCompare(b.n, 'fr', { numeric: true, sensitivity: 'base' }));
    const rech = this.$('[data-gp-recherche-modeles]');
    if (rech) { rech.hidden = liste.length <= 12; rech.value = ''; }
    grille.innerHTML = liste.map((m) => `<button type="button" class="gp-carte" role="option" aria-selected="${!e.modeleLibre && m.n === e.modele}" data-gp-modele-carte="${m.n.replace(/"/g, '&quot;')}" data-slug="${m.l}"><img src="${this.urlLogo(m.l)}" alt="" loading="lazy" width="56" height="56"><span>${m.n}</span></button>`).join('');
    if (aucun && !e.modeleLibre) this.modeleLibre(true, false);
  }

  choisirModele(nom, slug) {
    const e = this.piece.etat;
    e.modele = nom;
    e.logoModele = this.urlLogo(slug);
    e.modeleLibre = false;
    this.erreur('modele', '');
    this.$$('[data-gp-modele-carte]').forEach((b) => b.setAttribute('aria-selected', String(b.dataset.gpModeleCarte === nom)));
    const intro = this.$('[data-gp-modele-introuvable]');
    if (intro) intro.checked = false;
    this.$('[data-gp-bloc="modele-libre"]').hidden = true;
    const champ = this.$('[data-gp-champ="modele"]');
    if (champ) champ.value = '';
    this.changement('modele');
  }

  /** « Je ne trouve pas mon modèle » : description libre du logo, préparée par l'atelier (aperçu : cadre pointillé). */
  modeleLibre(actif, focus = true) {
    const e = this.piece.etat, champ = this.$('[data-gp-champ="modele"]');
    e.modeleLibre = actif;
    e.logoModele = null;
    this.$$('[data-gp-modele-carte]').forEach((b) => b.setAttribute('aria-selected', 'false'));
    this.$('[data-gp-bloc="modele-libre"]').hidden = !actif;
    e.modele = actif ? champ.value : '';
    if (actif && focus) setTimeout(() => champ.focus(), 0);
    this.changement('modele');
  }

  /* ---------- porte-clés assorti (porte-carte grise) : pastilles photo, « Sans » par défaut */
  porteCles() {
    const zone = this.$('[data-gp-porte-cles]');
    if (!zone) return;
    const nom = `gp-pc-${this.dataset.media}`;
    const sans = `<label class="gp-pastille gp-pastille--sans"><input type="radio" name="${nom}" value="" checked><span class="gp-pastille__rond" aria-hidden="true"></span><span class="gp-pastille__nom">${this.t.porte_cles_sans}</span></label>`;
    zone.innerHTML = sans + this.d.supports.porte_cles_assorti.filter((c) => this.d.options.porte_clefs.variantes[c.v])
      .map((c) => `<label class="gp-pastille gp-pastille--photo" style="--gp-c:${c.p}"><input type="radio" name="${nom}" value="${c.v}"><span class="gp-pastille__rond" aria-hidden="true"><img src="${c.mini}" alt="" loading="lazy" width="44" height="44"></span><span class="gp-pastille__nom">${this.valeur(c.v)}</span></label>`).join('');
  }
}

/* ------------------------------------------------------------------ Coffret (élément <guillot-coffret>, H41)
 * Un coffret empile des pièces (<guillot-perso data-gp-piece="i">, une par article du coffret) et, pour le coffret Jeune
 * Conducteur, le choix d'un Disque A. Une étape est ouverte à la fois ; l'aperçu de la galerie montre la pièce de cette étape.
 * Une seule ligne au panier (le coffret) : propriétés claires préfixées par la pièce (« Porte-carte grise – Marque »,
 * décision de Thomas du 18/10/2026), options payantes en lignes liées par _apo_order comme sur les fiches. */
const LIBELLES = { pcg: 'Porte-carte grise', pd: 'Porte-documents', pc: 'Porte-clés', pcb: 'Porte-carte bancaire' };

class GuillotCoffret extends HTMLElement {
  connectedCallback() {
    if (this.pret) return;
    this.pret = true;
    this.donneesBrutes ||= JSON.parse(this.querySelector(':scope > [data-gp-coffret-donnees]').textContent);
    this.d = this.donneesBrutes;
    const dec = document.createElement('textarea');
    this.t = {};
    Object.entries(this.d.t || {}).forEach(([k, v]) => { dec.innerHTML = v; this.t[k] = dec.value; });
    this.monnaie = new Intl.NumberFormat(this.d.langue || 'fr', { style: 'currency', currency: this.d.devise || 'EUR' });
    this.etapes = [...this.querySelectorAll('[data-gp-coffret-etape]')];
    this.cleStockage = `gp:${this.d.produit.handle}:coffret`;
    this.disque = '';
    this.meme = {};
    this.$$('[data-gp-meme]').forEach((c) => (this.meme[c.dataset.gpMeme] = true));   // coché par défaut
    let actif = 0;
    try {
      const st = JSON.parse(localStorage.getItem(this.cleStockage) || 'null');
      if (st?.v === 1) {
        this.disque = st.disque || ''; actif = Math.min(st.actif || 0, this.etapes.length - 1);
        if (st.qte) this.$('[data-gp-qte-champ]').value = st.qte;
        Object.assign(this.meme, st.meme || {});
      }
    } catch (e) { /* stockage indisponible */ }
    this.$$('[data-gp-meme]').forEach((c) => { c.checked = !!this.meme[c.dataset.gpMeme]; this.etapesMeme(c.dataset.gpMeme); });
    this.addEventListener('change', (ev) => {
      const c = ev.target.closest('[data-gp-meme]');
      if (!c) return;
      this.meme[c.dataset.gpMeme] = c.checked;
      this.etapesMeme(c.dataset.gpMeme);
      this.maj();
      this.sauver();
    });
    this.remplirDisques();
    this.addEventListener('click', (ev) => {
      const b = ev.target.closest('button');
      if (!b || !this.contains(b)) return;
      if (b.matches('[data-gp-aller]')) this.activer(Number(b.dataset.gpAller), true);
      else if (b.matches('[data-gp-suivant]')) this.suivant();
      else if (b.matches('[data-gp-disque]')) this.choisirDisque(b.dataset.gpDisque);
      else if (b.closest('[data-gp-coffret-achat]') && b.matches('[data-gp-qte]')) this.changerQte(Number(b.dataset.gpQte));
      else if (b.closest('[data-gp-coffret-achat]') && b.matches('[data-gp-ajouter]')) this.ajouter();
    });
    this.addEventListener('input', (ev) => { if (ev.target.closest('[data-gp-coffret-achat]') && ev.target.matches('[data-gp-qte-champ]')) { this.maj(); this.sauver(); } });
    this.activer(actif, false);
    customElements.whenDefined('guillot-perso').then(() => setTimeout(() => this.maj(), 0));
  }

  $(s) { return this.querySelector(s); }
  $$(s) { return [...this.querySelectorAll(s)]; }
  prix(c) { return this.monnaie.format(c / 100); }
  pieces() { return this.$$('guillot-perso'); }
  qte() { return Math.min(99, Math.max(1, parseInt(this.$('[data-gp-coffret-achat] [data-gp-qte-champ]').value, 10) || 1)); }

  sauver() {
    try { localStorage.setItem(this.cleStockage, JSON.stringify({ v: 1, disque: this.disque, actif: this.actif, qte: this.qte(), meme: this.meme })); } catch (e) { /* rien */ }
  }

  /** Ouvre une étape : sa pièce dessine l'aperçu sur la galerie, les autres retirent le leur. */
  activer(i, defiler) {
    this.actif = i;
    this.etapes.forEach((et, j) => { et.querySelector('[data-gp-coffret-corps]').hidden = j !== i; et.classList.toggle('is-ouverte', j === i); });
    this.$$('[data-gp-aller]').forEach((b) => b.setAttribute('aria-expanded', String(Number(b.dataset.gpAller) === i)));
    this.pieces().forEach((p) => { p.actif = p.closest('[data-gp-coffret-etape]') === this.etapes[i]; p.planifier?.(true); });
    this.calqueDisque();
    if (defiler) this.etapes[i].scrollIntoView({ block: 'start', behavior: reduit.matches ? 'auto' : 'smooth' });
    this.sauver();
  }

  suivant() {
    const et = this.etapes[this.actif], p = et.querySelector('guillot-perso');
    if (p && !p.valider()) return;
    if (!p && !this.disque) { this.erreurDisque(true); return; }
    this.activer(Math.min(this.actif + 1, this.etapes.length - 1), true);
  }

  /* ---------- même gravure que la première pièce (retour de Victor du 20/10/2026) */
  piece(i) { return this.$(`guillot-perso[data-gp-piece="${i}"]`); }
  source() { return this.$('guillot-perso'); }

  /** Gravure reprise : les étapes marque, modèle, texte et texte au recto de la pièce sont masquées. */
  etapesMeme(i) {
    const p = this.piece(i), on = !!this.meme[i];
    if (!p) return;
    ['marque', 'modele', 'texte', 'recto', 'variante'].forEach((k) => { const et = p.querySelector(`[data-gp-etape="${k}"]`); if (et) et.hidden = on; });
    if (!on) p.remplirVariantes?.();
    const r = this.$(`[data-gp-meme-resume="${i}"]`);
    if (r) r.hidden = !on;
  }

  /** Recopie la gravure de la première pièce dans les pièces cochées (seulement si elle a changé). */
  copierGravure() {
    const src = this.source();
    if (!src?.piece) return;
    const s = src.piece.etat;
    this.pieces().forEach((p) => {
      if (p === src || !p.piece) return;
      // même gravure : le logo du modèle est déjà payé sur la première pièce, il est inclus ici (Thomas, 22/10/2026)
      p.piece.modeleInclus = !!this.meme[p.dataset.gpPiece];
      if (!this.meme[p.dataset.gpPiece]) return;
      const e = p.piece.etat, avant = JSON.stringify(e);
      const aModele = !!p.querySelector('[data-gp-option="modele"]'), aTexte = !!p.querySelector('[data-gp-option="texte"]');
      const marqueChange = e.marque !== s.marque;
      Object.assign(e, { marque: s.marque, rectoOn: false, recto: '', variante: s.variante, varianteNom: s.varianteNom, logo: s.logo, logoUrl: s.logoUrl, mm: s.mm },
        aModele ? { modeleOn: s.modeleOn, modele: s.modele, logoModele: s.logoModele, modeleLibre: s.modeleLibre } : { modeleOn: false, modele: '', logoModele: null },
        aTexte ? { texteOn: s.texteOn, texte: s.texte } : { texteOn: false, texte: '' });
      if (JSON.stringify(e) === avant) return;
      p.synchroniserChamps?.();
      if (marqueChange) p.marqueChoisie?.();   // logo et taille propres à la pièce, repris de la liste des marques
      p.sauver?.();
      p.planifier?.();
    });
    // résumé de la gravure reprise, sous l'interrupteur
    const nom = (m) => (m || '').replace(/\s*\(.*?\)\s*/g, ' ').trim();
    const parts = [nom(s.marque), s.modeleOn && s.modele, s.texteOn && s.texte && `« ${s.texte} »`].filter(Boolean);
    this.$$('[data-gp-meme-resume]').forEach((r) => (r.textContent = parts.length ? parts.join(' · ') : this.t.meme_vide));
  }

  /* ---------- Disque A (coffret Jeune Conducteur) : grille des 42 modèles, photo de la fiche du disque */
  remplirDisques() {
    const g = this.$('[data-gp-disques]');
    if (!g) return;
    g.innerHTML = (this.d.disques || []).map((x) => `<button type="button" class="gp-carte gp-carte--disque" role="option" aria-selected="${x.v === this.disque}" data-gp-disque="${x.v.replace(/"/g, '&quot;')}">${x.img ? `<img src="${x.img}" alt="" loading="lazy" width="96" height="96">` : ''}<span>${x.v}</span></button>`).join('');
  }

  choisirDisque(v) {
    this.disque = v;
    this.$$('[data-gp-disque]').forEach((b) => b.setAttribute('aria-selected', String(b.dataset.gpDisque === v)));
    this.erreurDisque(false);
    this.calqueDisque();
    this.maj();
    this.sauver();
  }

  erreurDisque(on) {
    const el = this.$('[data-gp-erreur="disque"]');
    if (el) { el.hidden = !on; el.textContent = on ? this.t.erreur_disque : ''; }
    if (on) this.$('[data-gp-disques]')?.scrollIntoView({ block: 'center', behavior: reduit.matches ? 'auto' : 'smooth' });
  }

  /** Étape Disque A ouverte et disque choisi : sa photo remplace la photo du coffret dans la galerie. */
  calqueDisque() {
    const galerie = (this.closest('.shopify-section') || document).querySelector('product-media-gallery');
    if (!galerie) return;
    const id = this.dataset.media;
    let li = [...galerie.querySelectorAll(`[data-zoom-media-id="${id}"]`)];
    if (!li.length) li = [galerie.querySelector('.product-media-gallery__media')].filter(Boolean);
    const ouvert = this.etapes[this.actif]?.hasAttribute('data-gp-disque-etape');
    const x = (this.d.disques || []).find((d) => d.v === this.disque);
    li.map((n) => n.querySelector('.product-media')).filter(Boolean).forEach((cadre) => {
      let c = cadre.querySelector(':scope > .gp-calque[data-gp-piece="disque"]');
      if (!ouvert || !x?.grand) { c?.remove(); return; }
      if (!c) { c = document.createElement('div'); c.className = 'gp-calque gp-calque--disque'; c.dataset.gpPiece = 'disque'; cadre.appendChild(c); }
      c.innerHTML = `<img src="${x.grand}" alt="">`;
    });
  }

  /* ---------- récapitulatif, total, panier */
  maj() {
    this.copierGravure();
    const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);
    const li = [];
    let options = 0;
    this.etapes.forEach((et, i) => {
      const p = et.querySelector('guillot-perso');
      const titre = et.dataset.gpTitre;
      let lignes = [], fait = false;
      if (p?.piece) { lignes = p.lignesRecap(); options += p.piece.options().reduce((s, o) => s + o.prix, 0); fait = p.estValide(); }
      else if (!p) { if (this.disque) lignes = [`<li><span>${esc(this.t.disque)}</span><span>${esc(this.disque)}</span></li>`]; fait = !!this.disque; }
      li.push(`<li class="gp-recap__piece">${esc(titre)}</li>`, ...lignes);
      this.$(`[data-gp-aller="${i}"]`)?.classList.toggle('is-fait', fait);
    });
    this.$('[data-gp-coffret-achat] [data-gp-recap-liste]').innerHTML = li.join('');
    this.$('[data-gp-coffret-achat] [data-gp-total]').textContent = this.prix((this.d.produit.prix + options) * this.qte());
  }

  changerQte(delta) {
    const c = this.$('[data-gp-coffret-achat] [data-gp-qte-champ]');
    c.value = Math.min(99, Math.max(1, this.qte() + delta));
    this.maj();
    this.sauver();
  }

  async ajouter() {
    this.copierGravure();   // gravure reprise et options incluses à jour avant l'envoi
    const err = this.$('[data-gp-coffret-achat] [data-gp-erreur="panier"]');
    err.hidden = true;
    // première étape incomplète : on l'ouvre et on y montre l'erreur
    for (let i = 0; i < this.etapes.length; i++) {
      const p = this.etapes[i].querySelector('guillot-perso');
      if (p ? !p.estValide() : !this.disque) {
        this.activer(i, true);
        if (p) setTimeout(() => p.valider(), 350); else this.erreurDisque(true);
        return;
      }
    }
    const props = {}, options = [];
    this.etapes.forEach((et) => {
      const p = et.querySelector('guillot-perso');
      if (!p) { props['Disque A'] = this.disque; return; }
      const L = LIBELLES[p.piece.d.fiche.support];
      ['texte', 'verso', 'modele', 'recto'].forEach((k) => (p.piece.etat[k] = nettoyer(p.piece.etat[k], k === 'verso' && p.piece.d.fiche.support === 'pc' ? 3 : 1)));
      Object.assign(props, p.piece.proprietesCoffret(L));
      p.piece.options().forEach((o) => options.push({ ...o, apo: `${L} – ${o.apo}` }));
    });
    const bouton = this.$('[data-gp-coffret-achat] [data-gp-ajouter]'), libelle = bouton.querySelector('[data-gp-ajouter-texte]');
    const qte = this.qte(), id = String(Date.now());
    if (options.length) props._apo_order = id;
    const items = [{ id: this.d.produit.variante, quantity: qte, properties: props }];
    options.forEach((o) => items.push({ id: o.id, quantity: qte, properties: { _apo_option: o.apo, _apo_order: `Added by: ${id} * 1` } }));
    const sections = ['auto-gifts'];
    const tiroir = document.querySelector('cart-drawer');
    if (tiroir?.dataset.sectionId) sections.push(tiroir.dataset.sectionId);
    bouton.disabled = true;
    bouton.classList.add('is-loading');
    libelle.textContent = this.t.ajout_en_cours;
    try {
      // un fichier de gravure par pièce du coffret (porte-clés : recto et verso), joints à la ligne du coffret
      const fichiers = [];
      for (const et of this.etapes) {
        const p = et.querySelector('guillot-perso');
        if (!p?.piece) continue;
        const L = LIBELLES[p.piece.d.fiche.support];
        fichiers.push(...(await fichiersPiece(p.piece, `_Fichier gravure – ${L}`, `gravure-${this.d.produit.handle}-${p.piece.d.fiche.support}`)));
      }
      const r = await envoyerPanier(this.d.ajout, items, sections.join(','), fichiers);
      const rep = await r.json();
      if (!r.ok || rep.status) throw new Error(rep.description || rep.message || r.status);
      if (options.length) window.guillotPanier?.enregistrer(id, options);
      const compteur = document.querySelector('cart-icon')?.getCurrentItemCount?.();
      document.dispatchEvent(new CartUpdatedEvent({}, { itemCount: compteur != null ? compteur + items.reduce((s, i) => s + i.quantity, 0) : undefined, source: 'guillot-coffret', sections: rep.sections }));
      libelle.textContent = this.t.ajoute;
      try { localStorage.removeItem(this.cleStockage); this.pieces().forEach((p) => localStorage.removeItem(p.cleStockage)); } catch (e) { /* rien */ }
      setTimeout(() => (libelle.textContent = this.t.ajouter), 2500);
    } catch (e) {
      err.textContent = `${this.t.erreur_panier}${e?.message && !/^\d+$/.test(e.message) ? ` (${e.message})` : ''}`;
      err.hidden = false;
      libelle.textContent = this.t.ajouter;
    } finally {
      bouton.disabled = false;
      bouton.classList.remove('is-loading');
    }
  }
}

if (!customElements.get('guillot-coffret')) customElements.define('guillot-coffret', GuillotCoffret);

if (!customElements.get('guillot-perso')) customElements.define('guillot-perso', GuillotPerso);
